// ContentIQ — content project workspace (walking skeleton).
//
// Admin-only pilot (nav entries seeded by backend migration 0079, admin role
// only). A content project is always created FROM an existing NeTrA Program —
// pick a program, client + name ride along (Edward, 2026-08-06). Everything
// visible here renders live backend data; pieces that aren't built yet say so
// explicitly with their phase number. See backend docs/contentiq-architecture.md.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { EditableCell } from '../../components/InlineEditTable';
import CiqStageChip from '../../components/CiqStageChip';
import CiqBallChips from '../../components/CiqBallChips';
import { ballAgeDays, ballNames, pmBucket } from '../../utils/packageStatus';
import DataTable from '../../components/DataTable';
import { fmtDate } from '../../components/ZbSummary';
import useSort from '../../hooks/useSort';
import { downloadFileBlob } from '../../utils/ciqAssets';
import { assetPairs, bulletLines, isTwoTouchFormat } from '../../utils/ciqStrategyDoc';
import { useCurrentUser } from '../../context/UserContext';
import {
  BRAND, FONTS, HUES, INK, NEUTRAL, RADII, SHADOW, STATE, card, pageShell,
  buttonDisabled, buttonLink, buttonPrimary, buttonPrimarySmall, buttonSmall, displayFont,
} from '../../utils/wizardTheme';

const API = process.env.REACT_APP_API_URL;

const panelCard = {
  background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.border}`,
  borderRadius: RADII.card, boxShadow: SHADOW.card, padding: '18px 22px',
};
const panelTitle = { ...displayFont('16px'), letterSpacing: '0.01em' };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: '13px',
  fontFamily: FONTS.body, color: INK.primary, background: NEUTRAL.pageBg,
  border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small,
};
const thStyle = { textAlign: 'left', padding: '4px 8px', borderBottom: `2px solid ${NEUTRAL.border}`, fontSize: '12px', color: INK.secondary };
const tdStyle = { padding: '5px 8px', borderBottom: `1px solid ${NEUTRAL.borderSoft}`, verticalAlign: 'top' };

const FUNNEL_OPTIONS = ['Early', 'Mid', 'Late'].map(v => ({ value: v, label: v }));
const QUAL_OPTIONS = [
  ['none', 'No — standard registration only (client didn’t ask for qualification)'],
  ['client_provided', 'The client provided their questions — paste them below, used verbatim'],
  ['generate', 'Have ContentIQ propose BANT-style questions'],
  ['generate_plus_client', 'Both — the client requires specific questions (paste below, used verbatim) AND ContentIQ proposes additional BANT questions'],
];
// The modes whose paste box must show — used by Stage 1 and the Stage 3 wizard.
const QUAL_TEXT_MODES = ['client_provided', 'generate_plus_client'];
// The 2013 Activate persona matrix's phase names, kept as sub-labels.
const STAGE_PHASES_MATRIX = [['Early', 'Awareness'], ['Mid', 'Consideration'], ['Late', 'Justification']];

// Animated progress for the long synchronous AI calls (research ~1-2 min,
// strategy similar). We get no true progress from a single API call, so this
// is an honest approximation: an asymptotic bar (never claims done), a live
// elapsed clock (proves nothing is stalled), and stage-appropriate status
// messages keyed to typical timing. (Edward, 2026-08-11: "(up to a minute)"
// text alone reads as stalled — and it takes longer than a minute.)
export function GenerationProgress({ messages }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const pct = Math.min(95, Math.round(100 * (1 - Math.exp(-elapsed / 45))));
  const idx = Math.min(messages.length - 1, [8, 20, 35, 55, 80, 120].filter(t => elapsed >= t).length);
  const mins = Math.floor(elapsed / 60);
  const clock = mins > 0 ? `${mins}m ${elapsed % 60}s` : `${elapsed}s`;
  return (
    <div style={{ margin: '8px 0', maxWidth: '520px' }}>
      <div style={{ height: '6px', background: NEUTRAL.controlSoft, borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: BRAND.button, borderRadius: '3px', transition: 'width 1s linear' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: INK.secondary, marginTop: '4px' }}>
        <span>{messages[idx]}</span>
        <span style={{ whiteSpace: 'nowrap', marginLeft: '10px' }}>{clock} · typically 1–3 min</span>
      </div>
    </div>
  );
}

export const RESEARCH_PROGRESS_MSGS = [
  'Fetching your sources…',
  'Reading the core guidance docs…',
  'Analyzing the client material…',
  'Deriving personas and pain points…',
  'Drafting the message-map proposals…',
  'Still working — larger source sets take longer…',
  'Still working — hang tight…',
];
export const STRATEGY_PROGRESS_MSGS = [
  'Assembling the asset inventory and message map…',
  'Reading the core guidance docs…',
  'Weighing assets against personas and pain points…',
  'Selecting the strongest assets…',
  'Designing the qualification questions and flow…',
  'Writing rationales…',
  'Still working — hang tight…',
];
const LP_COPY_PROGRESS_MSGS = [
  'Loading the approved strategy and page plan…',
  'Reading the core guidance docs…',
  'Reading the featured assets…',
  'Writing headlines and body copy…',
  'Placing the approved questions on each page…',
  'Finishing thank-you and delivery copy…',
  'Still working — hang tight…',
];

// Deliverable statuses (Edward, 2026-08-27) — one concise workflow status
// per deliverable, stored on stage rows (3 strategy, 4 final copy,
// 5 telequal script; the backend creates missing rows). Replaces the old
// Mark-done checkbox on the deliverable panels; wording is a first pass.
export const DELIVERABLE_STATUSES = [
  ['draft', 'Draft'],
  ['pending_client', 'Sent to client'],
  ['client_approved', 'Client approved'],
  ['da_approved', 'DA approved'],
];
export function DeliverableStatus({ projectId, stageNumber, stage, onChanged }) {
  const known = DELIVERABLE_STATUSES.map(([v]) => v);
  const value = known.includes(stage?.approval_status) ? stage.approval_status : 'draft';
  return (
    <label style={{ fontSize: '12px', color: INK.secondary }}>
      Status:{' '}
      <select value={value} style={{ fontSize: '12px' }}
        onChange={async e => {
          const res = await axios.patch(
            `${API}/contentiq/projects/${projectId}/stages/${stageNumber}`,
            { approval_status: e.target.value });
          if (onChanged) onChanged(res.data);
        }}>
        {DELIVERABLE_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

// The strategy approval journey (Edward's flow + merged mockup, 2026-10-19):
// a five-step strip always shows where the document is — Generated →
// PM review → Client review → Approved → Page build — and beneath it only
// the CURRENT state's actions. The three-way revise dialog is the single
// revision mechanism in both review loops (the PM's and the client's), and
// a revision while the doc sits with the client opens the re-approval fork.
// Replaced StrategyNextStep (2026-10-15 → git history).
const fmtDay = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch (e) { return ''; }
};

function ReviseDialog({ projectId, round, onClose, onRegenerateNotes, onReload }) {
  // round: 'pm' | 'client' — who returned the document.
  const [mode, setMode] = useState('regen');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setErr('');
    if (mode === 'regen') {
      if (!text.trim()) { setErr('Type what should change first.'); return; }
      onClose();
      onRegenerateNotes(round === 'client'
        ? `The client returned these revision requests — apply each:\n${text.trim()}`
        : text.trim());
      return;
    }
    if (!file) { setErr('Pick the returned .docx first.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('mode', mode === 'doc_comments' ? 'comments' : 'edited');
      fd.append('round_label', round);
      fd.append('file', file);
      await axios.post(`${API}/contentiq/projects/${projectId}/strategy/revise-doc`,
        fd, { timeout: 240000 });
      onReload();
      onClose();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Revision failed.');
    } finally { setBusy(false); }
  };
  const row = (v, title, sub) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', border: `1.5px solid ${mode === v ? BRAND.button : NEUTRAL.border}`, background: mode === v ? HUES.strategy.tint : NEUTRAL.pageBg, borderRadius: RADII.control, padding: '10px 12px', cursor: 'pointer' }}>
      <input type="radio" name="revmode" checked={mode === v} onChange={() => setMode(v)} style={{ marginTop: '2px' }} />
      <span>
        <span style={{ fontWeight: 700, fontSize: '13px' }}>{title}</span><br />
        <span style={{ fontSize: '12px', color: INK.secondary }}>{sub}</span>
      </span>
    </label>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(19,24,67,0.35)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...card, width: 'min(560px, 92vw)', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ ...displayFont('16px', 650) }}>
          {round === 'client' ? 'Bring the client’s changes in' : 'Revise the strategy'}
        </div>
        {row('regen', round === 'client' ? 'Type their requests — regenerate applies them' : 'Regenerate with comments',
          'Describe the changes; everything not mentioned stands.')}
        {mode === 'regen' && (
          <textarea rows={4} value={text} onChange={e => setText(e.target.value)}
            placeholder={round === 'client' ? 'e.g. “Client wants Phase 2’s intro tightened…”' : 'e.g. “Tighten Page 1’s head; swap bullet 3…”'}
            style={{ border: `1px solid ${NEUTRAL.control}`, borderRadius: RADII.small, padding: '8px 10px', fontFamily: FONTS.body, fontSize: '12.5px', color: INK.body, resize: 'vertical' }} />
        )}
        {row('doc_comments', 'Upload the doc with comments',
          'We read the .docx’s comment bubbles as the revision asks.')}
        {row('doc_edited', 'Upload an edited version',
          'The edited document is authoritative — the strategy conforms to it.')}
        {mode !== 'regen' && (
          <input type="file" accept=".docx" aria-label="Returned document"
            onChange={e => setFile(e.target.files?.[0] || null)}
            style={{ fontSize: '12.5px', fontFamily: FONTS.body }} />
        )}
        {err && <div style={{ color: STATE.danger.text, fontSize: '12.5px' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy} style={buttonSmall}>Cancel</button>
          <button onClick={submit} disabled={busy} style={buttonPrimarySmall()}>
            {busy ? '⏳ Applying…' : 'Apply revision'}
          </button>
        </div>
      </div>
    </div>
  );
}

const JOURNEY_STEPS = ['Generated', 'PM review', 'Client review', 'Approved', 'Page build'];

export function StrategyJourney({ projectId, stage, meta, generatedAt, buildsCount = 0,
                                  strategyOnly = false, onChanged, onReload,
                                  onContinueToCopy, onRegenerateNotes, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [reviseRound, setReviseRound] = useState(null);  // null | 'pm' | 'client'
  const [guardOpen, setGuardOpen] = useState(false);
  const status = stage?.approval_status || 'draft';
  const ev = meta?.brief?.approval_events || {};
  const approved = ['client_approved', 'da_approved', 'approved'].includes(status);
  const current = buildsCount > 0 ? 4 : approved ? 3 : status === 'pending_client' ? 2 : 1;
  const act = async (action) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/contentiq/projects/${projectId}/strategy/approval`, { action });
      if (onChanged) onChanged(res.data.stage);
      if (onReload) onReload();  // approval_events ride the meta payload
    } finally { setBusy(false); }
  };
  const accept = () => (strategyOnly ? setGuardOpen(true) : act('accept_without_client'));

  const node = (i) => {
    const done = i < current;
    const cur = i === current;
    const sub = [generatedAt ? fmtDay(generatedAt) : '', '↻ revisions loop here',
                 '↻ their changes loop too', 'as-is or as revised', 'hand to the build team'][i];
    return (
      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', width: '116px', flexShrink: 0 }}>
        <div style={{ width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700,
          background: done ? STATE.done.tint : cur ? BRAND.button : NEUTRAL.pageBg,
          color: done ? STATE.done.deep : cur ? NEUTRAL.pageBg : INK.muted,
          border: done || cur ? 'none' : `2px solid ${NEUTRAL.controlSoft}` }}>
          {done ? '✓' : i + 1}
        </div>
        <div style={{ fontSize: '10.5px', fontWeight: cur ? 700 : 600, color: cur ? INK.primary : done ? STATE.done.text : INK.muted }}>
          {JOURNEY_STEPS[i]}
        </div>
        {sub ? <div style={{ fontSize: '9.5px', color: INK.muted }}>{sub}</div> : null}
      </div>
    );
  };
  const optionRow = (icon, iconBg, title, sub, button) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.tile, padding: '10px 14px' }}>
      <div style={{ width: '32px', height: '32px', borderRadius: RADII.small, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '13px' }}>{title}</div>
        <div style={{ fontSize: '12px', color: INK.secondary }}>{sub}</div>
      </div>
      {button}
    </div>
  );

  return (
    <div style={{ border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.tile, padding: '12px 14px 10px', marginBottom: '10px', background: NEUTRAL.pageBg }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {JOURNEY_STEPS.map((_, i) => [
          node(i),
          i < 4 && <div key={`c${i}`} style={{ flex: 1, height: '2px', marginTop: '11px', background: i < current ? STATE.done.border : NEUTRAL.controlSoft }} />,
        ])}
      </div>

      {compact ? null : (
      <div style={{ marginTop: '10px' }}>
        {current === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* "It's final" read as "no more changes" (the 2026-10-28
                walkthrough) — this step only means it goes to the client. */}
            {optionRow('✉', HUES.strategy.tint, 'Ready for the client to review',
              'Send them the strategy document yourself (nothing is emailed from here), then mark it sent. Record their answer here when it comes back.',
              <button onClick={() => act('send')} disabled={busy}
                style={{ ...buttonPrimarySmall(), ...(busy ? buttonDisabled : {}) }}>Mark sent to client</button>)}
            {optionRow('✎', STATE.gate.tint, 'Revise it first',
              'Regenerate with comments, upload the doc with comments, or upload an edited version.',
              <button onClick={() => setReviseRound('pm')} disabled={busy} style={buttonSmall}>Revise…</button>)}
            {optionRow('✓', STATE.done.tint, 'Accept without a client round',
              'Locks this version as approved internally; the pages come next.',
              <button onClick={accept} disabled={busy} style={buttonSmall}>Accept internally</button>)}
          </div>
        )}

        {current === 2 && !ev.client_round_revised_at && (
          <div style={{ background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`, borderRadius: RADII.tile, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '12.5px', color: STATE.gate.deep }}>
              ✉ Sent to the client{ev.sent_at ? ` ${fmtDay(ev.sent_at)}` : ''} — awaiting their word.
            </span>
            <span style={{ flexGrow: 1 }} />
            <button onClick={() => act('approved_as_is')} disabled={busy}
              style={{ ...buttonPrimarySmall(STATE.done.main) }}>✓ Approved as-is</button>
            <button onClick={() => setReviseRound('client')} disabled={busy} style={buttonSmall}>✎ They request changes…</button>
            <button onClick={() => act('back_to_pm')} disabled={busy}
              style={{ ...buttonSmall, color: INK.secondary, border: `1px solid ${NEUTRAL.controlSoft}` }}>↩ Back to PM review</button>
          </div>
        )}

        {current === 2 && ev.client_round_revised_at && (
          <div style={{ border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.tile, padding: '12px 14px' }}>
            <div style={{ fontSize: '12.5px', color: INK.body, marginBottom: '8px' }}>
              The client’s requested changes are in. Another look from them — or were the
              changes theirs, so it’s approved as revised?
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => act('send_reapproval')} disabled={busy}
                title="Send them the revised document yourself — nothing is emailed from here"
                style={{ ...buttonPrimarySmall(), ...(busy ? buttonDisabled : {}) }}>
                ✉ Mark re-sent for approval
              </button>
              <button onClick={() => act('approved_as_revised')} disabled={busy} style={buttonSmall}>
                ✓ Consider it approved — the changes were theirs
              </button>
            </div>
          </div>
        )}

        {current >= 3 && (
          <div style={{ background: STATE.done.tint, border: `1px solid ${STATE.done.border}`, borderRadius: RADII.tile, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '12.5px', color: STATE.done.deep }}>
              ✓ {status === 'da_approved' ? 'Accepted — internal approval' : 'Client approved'}
              {ev.approved_mode === 'as_revised' ? ', as revised' : ev.approved_mode === 'as_is' ? ', as-is' : ''}
              {ev.approved_at ? ` · ${fmtDay(ev.approved_at)}` : ''}
            </span>
            <span style={{ flexGrow: 1 }} />
            {current === 4 ? (
              <span style={{ fontSize: '12px', color: STATE.done.text }}>Pages are with the build team.</span>
            ) : (
              <>
                {onContinueToCopy && (
                  <button onClick={onContinueToCopy} style={buttonPrimary()}>Continue to Page build →</button>
                )}
                <button onClick={() => act('back_to_pm')} disabled={busy}
                  style={{ ...buttonSmall, color: INK.secondary, border: `1px solid ${NEUTRAL.controlSoft}` }}>↩ Back to PM review</button>
              </>
            )}
          </div>
        )}
      </div>
      )}

      {!compact && reviseRound && (
        <ReviseDialog projectId={projectId} round={reviseRound}
          onClose={() => setReviseRound(null)}
          onRegenerateNotes={onRegenerateNotes} onReload={onReload} />
      )}

      {!compact && guardOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(19,24,67,0.35)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ ...card, width: 'min(520px, 92vw)', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ ...displayFont('16px', 650) }}>This project was set up as strategy-only</div>
            <div style={{ fontSize: '13px', color: INK.body, lineHeight: 1.55 }}>
              The approved strategy already contains the full page set, and most strategies
              do become landing pages. Sure you won’t want them built?
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={async () => {
                setGuardOpen(false);
                await act('accept_without_client');
                const res = await axios.patch(`${API}/contentiq/projects/${projectId}/stages/3`, { done: true });
                if (onChanged) onChanged(res.data);
              }} style={buttonSmall}>Finish as strategy-only</button>
              <button onClick={() => { setGuardOpen(false); act('accept_without_client'); }}
                style={buttonPrimarySmall()}>Build the pages anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Stage2Panel({ project, personas, mapRows, onReload, onStageChanged }) {
  // Grid editor (Edward, 2026-08-10: "a grid would probably be more useful") —
  // every cell inline-editable via the shared EditableCell, plus an entry row
  // at the bottom. Persona is free text with archetype suggestions (datalist);
  // a name that exactly matches an archetype links the template, anything else
  // saves as a custom persona_override.
  const [form, setForm] = useState({ persona: '', pain_point: '', funnel_stage: 'Early', recommended_approach: '' });
  const [err, setErr] = useState('');
  // Matrix view (default) mirrors the 2013 Activate persona matrix: personas
  // across the top, buying phases down the side, trigger + messaging need in
  // each cell. List view keeps the flat grid for bulk entry.
  const [view, setView] = useState('matrix');
  const [cellAdd, setCellAdd] = useState(null);   // {persona, stage} with an open add-input
  const [cellPain, setCellPain] = useState('');
  const [newPersona, setNewPersona] = useState('');

  const personaPayload = (raw) => {
    const name = (raw || '').trim();
    const tpl = personas.find(pe => pe.role_category.toLowerCase() === name.toLowerCase());
    if (tpl) return { persona_template_id: tpl.id, persona_override: null };
    return { persona_template_id: null, persona_override: name ? { name } : null };
  };

  const saveRow = async (rowId, patch) => {
    await axios.put(`${API}/contentiq/projects/${project.id}/message-map/${rowId}`, patch);
    onReload();
  };

  const addRow = async () => {
    if (!form.pain_point.trim()) { setErr('Pain point is required.'); return; }
    try {
      await axios.post(`${API}/contentiq/projects/${project.id}/message-map`, {
        ...personaPayload(form.persona),
        pain_point: form.pain_point.trim(),
        funnel_stage: form.funnel_stage,
        recommended_approach: form.recommended_approach.trim() || null,
      });
      setForm({ persona: '', pain_point: '', funnel_stage: 'Early', recommended_approach: '' });
      setErr('');
      onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not add row.'); }
  };

  const removeRow = async (id) => {
    await axios.delete(`${API}/contentiq/projects/${project.id}/message-map/${id}`);
    onReload();
  };

  const addCellRow = async (personaName, stage) => {
    if (!cellPain.trim()) return;
    try {
      await axios.post(`${API}/contentiq/projects/${project.id}/message-map`, {
        ...personaPayload(personaName),
        pain_point: cellPain.trim(),
        funnel_stage: stage,
        recommended_approach: null,
      });
      setCellAdd(null); setCellPain(''); setErr('');
      if (personaName === newPersona.trim()) setNewPersona('');
      onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not add row.'); }
  };

  const onEnterAdd = e => { if (e.key === 'Enter') addRow(); };
  const footerInput = { ...inputStyle, padding: '4px 6px' };

  // Persona columns in order of first appearance; typing in "+ New persona"
  // shows the column immediately so its cells can take a first entry.
  const personaCols = [];
  mapRows.forEach(r => { const n = r.persona_name || 'Unassigned'; if (!personaCols.includes(n)) personaCols.push(n); });
  const pendingPersona = newPersona.trim() && !personaCols.includes(newPersona.trim()) ? newPersona.trim() : null;
  const allCols = pendingPersona ? [...personaCols, pendingPersona] : personaCols;

  return (
    <div style={{ ...panelCard, marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <strong style={panelTitle}>Buyer map</strong>
        <span style={{ display: 'inline-flex', border: `1px solid ${NEUTRAL.control}`, borderRadius: RADII.small, overflow: 'hidden' }}>
          {['matrix', 'list'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{ fontSize: '12px', cursor: 'pointer', padding: '3px 10px', border: 'none', fontFamily: FONTS.body,
                background: view === v ? HUES.matrix.main : NEUTRAL.pageBg, color: view === v ? NEUTRAL.pageBg : INK.body }}>
              {v === 'matrix' ? 'Matrix' : 'List'}
            </button>
          ))}
        </span>
        <span style={{ color: INK.secondary, fontSize: '12px' }}>persona × pain point × funnel stage · click any cell to edit</span>
      </div>

      {view === 'matrix' && (
        <>
          {allCols.length === 0 && (
            <div style={{ color: INK.secondary, fontSize: '13px', marginBottom: '6px' }}>
              No personas yet — type one under “+ New persona…”, accept research proposals on the
              Research step, or switch to the List view.
            </div>
          )}
          <div style={{ overflowX: 'auto', marginBottom: '6px' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '90px' }}>Phase</th>
                  {allCols.map(p => (
                    <th key={p} style={{ ...thStyle, minWidth: '220px' }}>
                      {p}{p === pendingPersona && <span style={{ color: INK.muted, fontWeight: 400 }}> (new — add a first entry below)</span>}
                    </th>
                  ))}
                  <th style={{ ...thStyle, minWidth: '170px' }}>
                    <input list="ciq-persona-suggestions-matrix" value={newPersona}
                      onChange={e => setNewPersona(e.target.value)}
                      placeholder="+ New persona…"
                      style={{ ...inputStyle, fontWeight: 400, fontSize: '12px', padding: '4px 6px' }} />
                    <datalist id="ciq-persona-suggestions-matrix">
                      {personas.map(pe => <option key={pe.id} value={pe.role_category} />)}
                    </datalist>
                  </th>
                </tr>
              </thead>
              <tbody>
                {STAGE_PHASES_MATRIX.map(([stg, sub]) => (
                  <tr key={stg}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700 }}>{stg}</div>
                      <div style={{ color: INK.muted, fontSize: '11px' }}>{sub}</div>
                    </td>
                    {allCols.map(p => (
                      <td key={p} style={{ ...tdStyle, background: NEUTRAL.band }}>
                        {mapRows.filter(r => (r.persona_name || 'Unassigned') === p && r.funnel_stage === stg).map(r => (
                          <div key={r.id} style={{ border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.small, padding: '4px 6px 6px', marginBottom: '4px', background: NEUTRAL.pageBg }}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <EditableCell value={r.pain_point}
                                  display={<span style={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'pre-wrap' }}>{r.pain_point}</span>}
                                  onSave={v => v.trim() ? saveRow(r.id, { pain_point: v.trim() })
                                                        : Promise.reject(new Error('Pain point is required'))} />
                              </div>
                              <button onClick={() => removeRow(r.id)}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK.muted, padding: 0 }}>✕</button>
                            </div>
                            <div style={{ fontSize: '12px', color: INK.body }}>
                              <EditableCell type="textarea" rows={2} value={r.recommended_approach || ''}
                                placeholder="content & messaging approach" emptyText="+ approach"
                                onSave={v => saveRow(r.id, { recommended_approach: v.trim() || null })} />
                            </div>
                          </div>
                        ))}
                        {cellAdd && cellAdd.persona === p && cellAdd.stage === stg ? (
                          <input autoFocus value={cellPain} onChange={e => setCellPain(e.target.value)}
                            placeholder="pain point / trigger" style={{ ...inputStyle, fontSize: '12px' }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') addCellRow(p, stg);
                              else if (e.key === 'Escape') { setCellAdd(null); setCellPain(''); }
                            }}
                            onBlur={() => { if (!cellPain.trim()) setCellAdd(null); }} />
                        ) : (
                          <button onClick={() => { setCellAdd({ persona: p, stage: stg }); setCellPain(''); }}
                            title={`Add a ${stg}-phase entry for ${p}`}
                            style={{ border: `1px dashed ${NEUTRAL.control}`, background: 'none', cursor: 'pointer', color: INK.muted, borderRadius: RADII.small, fontSize: '12px', padding: '2px 8px' }}>
                            +
                          </button>
                        )}
                      </td>
                    ))}
                    <td style={tdStyle} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'list' && mapRows.length === 0 && (
        <div style={{ color: INK.secondary, fontSize: '13px', marginBottom: '6px' }}>
          No rows yet — add one in the bottom row of the grid, or run research on the Research step and accept its proposals.
        </div>
      )}
      {view === 'list' && (
      <div style={{ overflowX: 'auto', marginBottom: '6px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '20%' }}>Persona</th>
              <th style={{ ...thStyle, width: '70px' }}>Stage</th>
              <th style={{ ...thStyle, width: '30%' }}>Pain point</th>
              <th style={thStyle}>Recommended approach</th>
              <th style={{ ...thStyle, width: '28px' }} />
            </tr>
          </thead>
          <tbody>
            {mapRows.map(r => (
              <tr key={r.id}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>
                  <EditableCell value={r.persona_name || ''} placeholder="persona"
                    onSave={v => saveRow(r.id, personaPayload(v))} />
                </td>
                <td style={tdStyle}>
                  <EditableCell type="select" value={r.funnel_stage} options={FUNNEL_OPTIONS}
                    onSave={v => saveRow(r.id, { funnel_stage: v })} />
                </td>
                <td style={tdStyle}>
                  <EditableCell value={r.pain_point}
                    onSave={v => v.trim() ? saveRow(r.id, { pain_point: v.trim() })
                                          : Promise.reject(new Error('Pain point is required'))} />
                </td>
                <td style={{ ...tdStyle, color: INK.body }}>
                  <EditableCell type="textarea" rows={2} value={r.recommended_approach || ''}
                    onSave={v => saveRow(r.id, { recommended_approach: v.trim() || null })} />
                </td>
                <td style={tdStyle}>
                  <button onClick={() => removeRow(r.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK.muted }}>✕</button>
                </td>
              </tr>
            ))}
            {/* Entry row — always at the bottom of the grid */}
            <tr style={{ background: NEUTRAL.band }}>
              <td style={tdStyle}>
                <input list="ciq-persona-suggestions" value={form.persona} onKeyDown={onEnterAdd}
                  onChange={e => setForm(f => ({ ...f, persona: e.target.value }))}
                  placeholder="persona — type or pick" style={footerInput} />
                <datalist id="ciq-persona-suggestions">
                  {personas.map(pe => <option key={pe.id} value={pe.role_category} />)}
                </datalist>
              </td>
              <td style={tdStyle}>
                <select value={form.funnel_stage} onChange={e => setForm(f => ({ ...f, funnel_stage: e.target.value }))} style={footerInput}>
                  <option>Early</option><option>Mid</option><option>Late</option>
                </select>
              </td>
              <td style={tdStyle}>
                <input value={form.pain_point} onKeyDown={onEnterAdd}
                  onChange={e => setForm(f => ({ ...f, pain_point: e.target.value }))}
                  placeholder="pain point (required)" style={footerInput} />
              </td>
              <td style={tdStyle}>
                <input value={form.recommended_approach} onKeyDown={onEnterAdd}
                  onChange={e => setForm(f => ({ ...f, recommended_approach: e.target.value }))}
                  placeholder="recommended approach" style={footerInput} />
              </td>
              <td style={tdStyle}>
                <button onClick={addRow} title="Add row" style={{ ...buttonSmall, whiteSpace: 'nowrap' }}>+ Add</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      )}
      {err && <div style={{ color: STATE.danger.text, fontSize: '12px', marginTop: '4px' }}>{err}</div>}
    </div>
  );
}

const STRUCTURE_OPTIONS = [
  ['single', 'One landing page for all buyers at all stages'],
  ['per_persona', 'One landing page per buyer type'],
  ['per_persona_stage', 'One landing page per buyer type per funnel stage'],
  ['custom', 'Custom — describe it'],
];

export function Stage3Panel({ project, strategy, flow, meta, onReload, onStageChanged, onOpenBuyerMap, onContinueToCopy, buildsCount = 0, strategyOnly = false }) {
  // The pilot's second AI touchpoint: one call grounded in the core docs +
  // this project's research + message map -> reviewable rows, never a blob.
  // Regeneration keeps rows the PM edited (pm_edited flag, backend-enforced).
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState('');
  // The document IS the key output (Edward, 2026-08-26) — it leads and is the
  // default once a strategy exists; the page plan is Stage-4 plumbing, demoted
  // to a small link. 'items' stays the default only while nothing is built.
  // Data arrives after mount (panels stay mounted), so the default flips via
  // an effect — but never over an explicit tab choice.
  const [view, setView] = useState('items');  // 'doc' | 'items' | 'pages'
  const viewChosen = useRef(false);
  useEffect(() => {
    if (!viewChosen.current && (strategy.length || flow)) setView('doc');
  }, [strategy.length, flow]);
  // The client-asset pool + which of them THIS build may select from
  // (Edward, 2026-08-24: "recommend which one to use" — the PM shortlists,
  // generation recommends from the shortlist). null = all selected.
  const [poolAssets, setPoolAssets] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  // PM override (Edward's Indeed SMB trial, 2026-09-23): pick the asset(s)
  // outright — the pick is FINAL, regeneration builds FOR them, and the
  // "why" rides into the prompt + is stored on the brief.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [pinIds, setPinIds] = useState([]);
  const [pinWhy, setPinWhy] = useState('');
  const pinnedIds = meta?.brief?.pinned_asset_ids || [];
  const pinnedWhy = meta?.brief?.override_note || '';
  // The doc's chapter selection — saved per project, honored by the in-app
  // view AND the docx download identically (Edward, 2026-09-23).
  const [docSections, setDocSections] = useState(null);
  useEffect(() => { setDocSections(meta?.brief?.doc_sections || null); }, [meta?.brief]);
  useEffect(() => {
    axios.get(`${API}/contentiq/projects/${project.id}/sources`)
      .then(r => setPoolAssets(r.data.filter(a => a.is_promotable && a.kind !== 'brand')))
      .catch(() => setPoolAssets([]));
  }, [project.id]);
  useEffect(() => {
    if ((meta?.brief?.asset_ids || []).length) setSelectedAssetIds(meta.brief.asset_ids);
  }, [meta?.brief]);
  const effectiveSelected = selectedAssetIds ?? poolAssets.map(a => a.id);
  // The items ARE the recommendation — the checkboxes above are only the
  // shortlist generation may pick from (Edward, 2026-08-27: three checked
  // boxes read as "three selected" when the setup said one).
  const recommendedIds = new Set((strategy || []).map(it => it.asset_id).filter(Boolean));
  const recommendedPairs = isTwoTouchFormat(meta?.format) ? assetPairs(meta?.page_plan, strategy) : [];
  const toggleAssetSel = (id) => {
    const cur = new Set(effectiveSelected);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setSelectedAssetIds([...cur]);
  };
  // Build-intent wizard (Edward, 2026-08-11): confirm WHAT to build before
  // generating — page structure + interactivity are hard constraints, so a
  // CS project can never come back with a DA-shaped interactive flow.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [brief, setBrief] = useState({ structure: 'single', custom_note: '', interactive: false, notes: '' });
  // R2.4 lanes: the upfront choice for a one-asset-per-build recipe with
  // several included assets — N lanes in one strategy, or a single build.
  // Seeded from the stored plan so a regenerate keeps the prior answer.
  const [laneChoice, setLaneChoice] = useState('lanes');
  // With lanes: BANT/script per lane (common) or one generic set — seeded
  // from the stored brief so a regenerate keeps the prior answer.
  const [lanesCustom, setLanesCustom] = useState('per_lane');
  useEffect(() => {
    if (meta?.brief) setBrief(b => ({ ...b, ...meta.brief }));
    if (meta?.brief) setLaneChoice((meta.brief.lanes || []).length > 0 ? 'lanes'
      : meta.brief.asset_count === 1 && (meta.brief.asset_ids || []).length > 1 ? 'single' : 'lanes');
    if (meta?.brief?.lanes_custom) setLanesCustom(meta.brief.lanes_custom);
  }, [meta?.brief]);

  const [qual, setQual] = useState({ mode: 'none', text: '' });

  const openWizard = async () => {
    if (strategy.length > 0 && !window.suppressConfirm &&
        !window.confirm('Regenerate the strategy? AI-generated rows are replaced; rows you have edited are kept.')) return;
    if (meta?.format?.strategy_recipe) {
      // Recipe formats: the recipe decides structure and question policy —
      // no legacy knobs to prefill (Edward, 2026-10-15: the old dialog
      // "doesn't make sense anymore" on a recipe product).
      setWizardOpen(true);
      return;
    }
    // Prefill: the saved brief wins; else Stage 1's information-gathering answer.
    if (meta?.brief?.qual_questions) {
      setQual({ mode: meta.brief.qual_questions.mode || 'none', text: meta.brief.qual_questions.text || '' });
    } else {
      try {
        const res = await axios.get(`${API}/contentiq/projects/${project.id}/context`);
        const mi = res.data.manual_input || {};
        // Stage 1 captures what the client SUPPLIED (bant/custom); the mode
        // decision lives here. BANT supplied -> verbatim; only custom ->
        // custom verbatim + propose BANT; neither -> legacy fields or none.
        const bant = (mi.client_bant_questions || '').trim();
        const custom = (mi.client_custom_questions || '').trim();
        if (bant) {
          setQual({ mode: 'client_provided',
            text: custom ? `${bant}\n\nClient's required custom questions:\n${custom}` : bant });
        } else if (custom) {
          setQual({ mode: 'generate_plus_client', text: custom });
        } else {
          setQual({ mode: mi.qual_questions_mode || 'none', text: mi.qual_questions_text || '' });
        }
      } catch (e) { setQual({ mode: 'none', text: '' }); }
    }
    setWizardOpen(true);
  };

  const generate = async () => {
    setWizardOpen(false);
    setGenerating(true); setMsg('');
    try {
      if (meta?.format?.strategy_recipe) {
        // The recipe governs structure and questions — only the optional
        // constraint note, the asset set and (for a pathless product with
        // several assets) the lane plan travel.
        const r = meta.format.strategy_recipe;
        const laneEligible = !r.paths && !r.touches && effectiveSelected.length > 1;
        await axios.post(`${API}/contentiq/projects/${project.id}/strategy/generate`, {
          notes: brief.notes || null,
          asset_ids: effectiveSelected,
          lanes: laneEligible && laneChoice === 'lanes'
            ? effectiveSelected.map(id => ({
                name: poolAssets.find(a => a.id === id)?.title || null,
                asset_ids: [id],
              }))
            : [],
          lanes_custom: laneEligible && laneChoice === 'lanes' ? lanesCustom : undefined,
        }, { timeout: 240000 });
      } else {
        await axios.post(`${API}/contentiq/projects/${project.id}/strategy/generate`, {
          structure: brief.structure,
          custom_note: brief.structure === 'custom' ? (brief.custom_note || '') : null,
          interactive: meta?.format?.has_branching ? !!brief.interactive : false,
          notes: brief.notes || null,
          qual_questions: { mode: qual.mode, text: QUAL_TEXT_MODES.includes(qual.mode) ? (qual.text || '') : '' },
          asset_ids: effectiveSelected,
        }, { timeout: 240000 });
      }
      onReload();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  const regenerateWith = async (body) => {
    setGenerating(true); setMsg('');
    try {
      await axios.post(`${API}/contentiq/projects/${project.id}/strategy/generate`,
        body, { timeout: 240000 });
      onReload();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Generation failed.');
    } finally { setGenerating(false); }
  };
  const pinAndRegenerate = async () => {
    if (!pinIds.length) return;
    setOverrideOpen(false);
    await regenerateWith({ pinned_asset_ids: pinIds, override_note: pinWhy.trim() || null });
    setPinIds([]); setPinWhy('');
  };
  const clearPin = async () => {
    if (!window.suppressConfirm &&
        !window.confirm('Clear your pinned choice and let ContentIQ recommend again? This regenerates the strategy.')) return;
    await regenerateWith({ pinned_asset_ids: [], override_note: null });
  };

  const secOn = (k) => !docSections || docSections[k] !== false;
  const toggleSection = async (k) => {
    const next = { ...(docSections || {}), [k]: !secOn(k) };
    setDocSections(next);
    try {
      await axios.patch(`${API}/contentiq/projects/${project.id}/strategy/doc-sections`,
        { sections: next });
    } catch (e) { /* the local view already reflects it; retried on next toggle */ }
  };

  const saveItem = async (id, patch) => {
    await axios.put(`${API}/contentiq/projects/${project.id}/strategy/${id}`, patch);
    onReload();
  };
  const removeItem = async (id) => {
    await axios.delete(`${API}/contentiq/projects/${project.id}/strategy/${id}`);
    onReload();
  };

  // Grouped by persona — the strategy document's own reading order.
  const groups = [];
  strategy.forEach(s => {
    const key = s.persona_name || 'Unassigned';
    let g = groups.find(x => x.persona === key);
    if (!g) { g = { persona: key, items: [] }; groups.push(g); }
    g.items.push(s);
  });

  const stage = (project.stages || []).find(st => st.stage_number === 3);
  return (
    <div style={{ ...panelCard, marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <strong style={panelTitle}>Content strategy</strong>
        <span style={{ color: INK.secondary, fontSize: '12px' }}>
          The strategy document is the client-facing proposal.
        </span>
        {/* The merged mockup's quiet toolbar (Edward, 2026-10-19): the old
            Document/Edit-rows view TOGGLE is gone — the document is the
            view; editing and the page plan are toolbar links on it. */}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '14px', alignItems: 'center' }}>
          <button onClick={() => { viewChosen.current = true; setView(view === 'items' ? 'doc' : 'items'); }}
            style={{ ...buttonLink, fontSize: '12px', color: view === 'items' ? HUES.strategy.main : BRAND.link }}>
            {view === 'items' ? 'Document' : '✎ Edit rows'}
          </button>
          <button onClick={() => { viewChosen.current = true; setView(view === 'pages' ? 'doc' : 'pages'); }}
            style={{ ...buttonLink, fontSize: '12px', color: view === 'pages' ? HUES.strategy.main : BRAND.link }}>
            Page plan
          </button>
        </span>
      </div>

      {/* The approval journey (Edward's flow, 2026-10-19) — strip + the
          current state's actions; renders once a strategy exists. */}
      {(strategy.length > 0 || flow) && (
        <StrategyJourney projectId={project.id} stage={stage} meta={meta}
          generatedAt={flow?.generated_at} buildsCount={buildsCount}
          strategyOnly={strategyOnly} onChanged={onStageChanged} onReload={onReload}
          onContinueToCopy={onContinueToCopy}
          onRegenerateNotes={(notes) => regenerateWith({ notes })} />
      )}

      {/* The build's asset shortlist — generation recommends from these. */}
      <div style={{ background: NEUTRAL.band, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.tile, padding: '10px 14px', marginBottom: '10px' }}>
        {strategy.length > 0 && recommendedIds.size > 0 ? (
          /* Post-generation: the conversational recommendation (Edward,
             2026-08-27 — "here are the assets I reviewed, here's the one I
             recommend, and here's why"). The checkboxes move behind Adjust. */
          <div style={{ fontSize: '13px', color: INK.body, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontWeight: 700, color: INK.primary }}>Here's where I landed</div>
            {poolAssets.length > 0 && (
              <>
                <div>I reviewed {poolAssets.length} client asset{poolAssets.length === 1 ? '' : 's'}:</div>
                <ul style={{ margin: '0 0 2px', paddingLeft: '22px' }}>
                  {poolAssets.map(a => (
                    <li key={a.id} style={{ margin: '1px 0' }}>
                      {a.title}{a.has_client_copy ? ' (client copy supplied)' : ''}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div>
              {pinnedIds.length > 0
                ? (pinnedIds.length === 1 ? 'Your pick (pinned):' : 'Your picks (pinned):')
                : recommendedPairs.length > 0
                  ? (recommendedPairs.length === 1 ? 'I recommend this pair:' : `I recommend these ${recommendedPairs.length} pairs:`)
                  : (recommendedIds.size === 1 ? 'I recommend:' : `I recommend these ${recommendedIds.size}:`)}
            </div>
            {recommendedPairs.length > 0 ? recommendedPairs.map((pr, i) => (
              // Two-touch (Edward, 2026-10-28): the recommendation reads as
              // pairs — one asset per engagement phase — not a flat list.
              <div key={i} style={{ borderLeft: `3px solid ${HUES.strategy.main}`, paddingLeft: '10px' }}>
                <div style={{ fontWeight: 700 }}>{pr.name}</div>
                {pr.members.map(m => (
                  <div key={m.assetId} style={{ margin: '2px 0' }}>
                    <span style={{ color: INK.secondary }}>Engagement Phase {m.phase}: </span>
                    <span style={{ fontWeight: 700 }}>
                      {pinnedIds.includes(m.assetId) ? '📌' : '★'} {m.item?.asset_title || `asset ${m.assetId}`}
                    </span>
                    {m.item?.rationale && <span style={{ color: INK.secondary }}> — {m.item.rationale}</span>}
                  </div>
                ))}
              </div>
            )) : [...recommendedIds].map(rid => {
              const it = strategy.find(x => x.asset_id === rid) || {};
              return (
                <div key={rid} style={{ borderLeft: `3px solid ${HUES.strategy.main}`, paddingLeft: '10px' }}>
                  <span style={{ fontWeight: 700 }}>{pinnedIds.includes(rid) ? '📌' : '★'} {it.asset_title || `asset ${rid}`}</span>
                  {it.rationale && <span style={{ color: INK.secondary }}> — {it.rationale}</span>}
                </div>
              );
            })}
            {pinnedIds.length > 0 && (
              <div style={{ fontSize: '12px', color: INK.secondary }}>
                You chose {pinnedIds.length === 1 ? 'this asset' : 'these assets'} yourself
                {pinnedWhy ? <> — “{pinnedWhy}”</> : ''}. ContentIQ built the strategy for
                {pinnedIds.length === 1 ? ' it' : ' them'} without re-choosing.{' '}
                <button onClick={clearPin} style={{ ...buttonLink, fontSize: '12px' }}>
                  Clear the pin &amp; let it recommend again
                </button>
              </div>
            )}
            <div style={{ fontSize: '12px', color: INK.secondary }}>
              The full content strategy below shows the pages, copy, and questions built around
              {recommendedIds.size === 1 ? ' it' : ' them'}.
              {onOpenBuyerMap && (
                <>
                  {' '}The reasoning lives in the{' '}
                  <button onClick={onOpenBuyerMap} style={{ ...buttonLink, fontSize: '12px' }}>Buyer map →</button>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {pinnedIds.length === 0 && (
                <button onClick={() => {
                  setOverrideOpen(o => !o);
                  if (!overrideOpen) { setPinIds([...recommendedIds].filter(Boolean)); setAdjustOpen(false); }
                }}
                  style={{ ...buttonLink, fontSize: '11.5px' }}>
                  {overrideOpen ? '▾ Cancel' : '▸ Use a different asset — my pick is final'}
                </button>
              )}
              <button onClick={() => setAdjustOpen(o => !o)}
                style={{ ...buttonLink, fontSize: '11.5px', fontWeight: 400 }}>
                {adjustOpen ? '▾ Hide the shortlist' : '▸ Adjust the shortlist (then regenerate)'}
              </button>
            </div>
            {overrideOpen && (
              <div style={{ border: `2px solid ${HUES.strategy.main}`, borderRadius: RADII.tile, padding: '10px 14px', background: NEUTRAL.pageBg }}>
                <div style={{ fontWeight: 700, fontSize: '12.5px', marginBottom: '4px' }}>
                  Pick the asset{(meta?.brief?.asset_count || 1) === 1 ? '' : 's'} to build for
                  {meta?.brief?.asset_count ? ` (this build wants ${meta.brief.asset_count})` : ''}
                </div>
                {poolAssets.map(a => (
                  <label key={a.id} style={{ display: 'block', fontSize: '12.5px', margin: '2px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={pinIds.includes(a.id)}
                      onChange={() => setPinIds(ids => ids.includes(a.id) ? ids.filter(x => x !== a.id) : [...ids, a.id])} />
                    {' '}{a.title}{a.has_client_copy ? ' (client copy supplied)' : ''}
                  </label>
                ))}
                <textarea value={pinWhy} onChange={e => setPinWhy(e.target.value)} rows={2}
                  placeholder="Why this choice? Your reasoning becomes the document's argument — and teaches ContentIQ."
                  style={{ ...inputStyle, width: '100%', resize: 'vertical', margin: '6px 0 4px' }} />
                <button onClick={pinAndRegenerate} disabled={generating || pinIds.length === 0}
                  style={pinIds.length && !generating ? buttonPrimarySmall(HUES.strategy.main)
                    : { ...buttonPrimarySmall(), ...buttonDisabled }}>
                  Pin and regenerate
                </button>
                <span style={{ fontSize: '11px', color: INK.secondary, marginLeft: '8px' }}>
                  Your pick is final — the strategy is rebuilt around it, no re-choosing.
                </span>
              </div>
            )}
            {adjustOpen && (
              <div style={{ marginTop: '4px' }}>
                {poolAssets.map(a => (
                  <label key={a.id} style={{ display: 'block', fontSize: '12.5px', margin: '2px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={effectiveSelected.includes(a.id)} onChange={() => toggleAssetSel(a.id)} />
                    {' '}{a.title}
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ fontSize: '12px', fontWeight: 700, color: INK.primary, marginBottom: '4px' }}>
              Asset shortlist
              <span style={{ fontWeight: 400, color: INK.secondary }}>
                {meta?.brief?.asset_count
                  ? ` — generation picks the best ${meta.brief.asset_count} from these`
                  : ' — generation picks from these'}
              </span>
            </div>
            {poolAssets.length === 0 ? (
              <div style={{ fontSize: '12px', color: STATE.gate.text }}>
                No client assets yet — add the content the campaign promotes in the
                “Client assets” setup step, then come back to generate.
              </div>
            ) : poolAssets.map(a => (
              <label key={a.id} style={{ display: 'block', fontSize: '12.5px', margin: '2px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={effectiveSelected.includes(a.id)} onChange={() => toggleAssetSel(a.id)} />
                {' '}{a.asset_type === 'source_url' ? '🔗' : '📄'} {a.title}
                {a.has_client_copy && (
                  <span style={{ background: STATE.done.tint, color: STATE.done.text, borderRadius: '10px', padding: '0 6px', fontSize: '10.5px', fontWeight: 600, marginLeft: '6px' }}>
                    ✓ client copy
                  </span>
                )}
              </label>
            ))}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
        {/* Regenerating is secondary once a strategy stands — the journey's
            approval actions above are the step's main path. */}
        <button onClick={openWizard} disabled={generating || poolAssets.length === 0 || effectiveSelected.length === 0}
          style={!(poolAssets.length && effectiveSelected.length && !generating)
            ? { ...(strategy.length || flow ? buttonSmall : buttonPrimarySmall()), ...buttonDisabled }
            : (strategy.length || flow ? buttonSmall : buttonPrimarySmall(HUES.strategy.main))}>
          {generating ? '⏳ Generating…' : (strategy.length || flow ? '↻ Regenerate strategy' : 'Generate strategy')}
        </button>
        <span style={{ color: INK.secondary, fontSize: '12px' }}>
          {poolAssets.length === 0
            ? 'Needs at least one client asset (add it on the Client assets step) before it can build.'
            : effectiveSelected.length === 0
            ? 'Include at least one asset in the build.'
            : 'Recommends assets from your shortlist, grounded in the research, the buyer map and the product\u2019s rules. Rows you edit are kept on regenerate.'}
        </span>
      </div>
      {wizardOpen && (
        <div style={{ border: `2px solid ${HUES.strategy.main}`, borderRadius: RADII.tile, padding: '14px 16px', margin: '10px 0', background: HUES.strategy.tint, maxWidth: '640px' }}>
          <div style={{ fontWeight: 700, marginBottom: '6px' }}>What should this strategy build?</div>
          {meta?.format?.strategy_recipe ? (() => {
            const r = meta.format.strategy_recipe;
            const n = effectiveSelected.length;
            const phones = (r.touchpoints || []).filter(tp => tp.kind === 'phone');
            const placement = r.questions?.bant?.placement || {};
            const tpName = {};
            (r.touchpoints || []).forEach(tp => { tpName[tp.key] = tp.name; });
            return (
              <div style={{ fontSize: '12.5px', color: INK.body, display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                <div>
                  <b>{meta.format.format_name}</b> builds from its recipe — nothing to configure here:
                </div>
                {r.paths ? (
                  <div>• One page set per {r.paths.unit} — your {n} included asset{n === 1 ? '' : 's'} = {n} parallel path{n === 1 ? '' : 's'}.</div>
                ) : r.touches ? (
                  <div>• {r.touches.count} sequential touches to the same lead, one asset each.</div>
                ) : n > 1 ? (
                  /* R2.4's upfront rules check (Edward): a one-asset-per-build
                     product with several included assets — lanes or a single
                     build, decided HERE, not discovered after generation. */
                  <div style={{ border: `1px solid ${HUES.strategy.border}`, borderRadius: RADII.small, background: NEUTRAL.pageBg, padding: '8px 10px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      This product features one asset per build — you included {n}.
                    </div>
                    <label style={{ display: 'block', margin: '2px 0', cursor: 'pointer' }}>
                      <input type="radio" name="ciq-lanes" checked={laneChoice === 'lanes'}
                        onChange={() => setLaneChoice('lanes')} />{' '}
                      Build <b>{n} lanes</b> in one strategy — one build per asset (recommended)
                    </label>
                    <label style={{ display: 'block', margin: '2px 0', cursor: 'pointer' }}>
                      <input type="radio" name="ciq-lanes" checked={laneChoice === 'single'}
                        onChange={() => setLaneChoice('single')} />{' '}
                      Single build — ContentIQ recommends one of the {n}
                    </label>
                    {laneChoice === 'lanes' && (
                      <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${HUES.strategy.border}` }}>
                        <div style={{ fontWeight: 600, marginBottom: '2px' }}>BANT questions &amp; script:</div>
                        <label style={{ display: 'block', margin: '2px 0', cursor: 'pointer' }}>
                          <input type="radio" name="ciq-lanescustom" checked={lanesCustom === 'per_lane'}
                            onChange={() => setLanesCustom('per_lane')} />{' '}
                          <b>Customized per lane</b> (common for multiple lanes)
                        </label>
                        <label style={{ display: 'block', margin: '2px 0', cursor: 'pointer' }}>
                          <input type="radio" name="ciq-lanescustom" checked={lanesCustom === 'generic'}
                            onChange={() => setLanesCustom('generic')} />{' '}
                          <b>Generic</b> — one shared set; only the asset title swaps
                        </label>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>• One build featuring one asset.</div>
                )}
                {phones.map(tp => (
                  <div key={tp.key}>• 📞 {tpName[tp.key]}{placement[tp.key]
                    ? ` — ${Array.isArray(placement[tp.key]) && placement[tp.key][0] !== 'remaining'
                        ? placement[tp.key].join(' + ') : 'the remaining BANT dimensions'}` : ''}</div>
                ))}
                <div style={{ color: INK.secondary }}>
                  Client questions come from the Questions &amp; privacy step: supplied BANT is used
                  verbatim (replacing ours); qualifying questions seed the compelling-need questions.
                  Change them there, then regenerate.
                </div>
              </div>
            );
          })() : (
          <>
          <div style={{ fontSize: '12px', color: INK.secondary, marginBottom: '8px' }}>
            The analysis covers every buyer and pain point either way — this decides how it becomes actual pages.
          </div>
          {STRUCTURE_OPTIONS.map(([v, label]) => (
            <label key={v} style={{ display: 'block', fontSize: '13px', margin: '3px 0', cursor: 'pointer' }}>
              <input type="radio" name="ciq-structure" checked={brief.structure === v}
                onChange={() => setBrief(b => ({ ...b, structure: v }))} /> {label}
            </label>
          ))}
          {brief.structure === 'custom' && (
            <input value={brief.custom_note || ''} placeholder="Describe the page structure you want"
              onChange={e => setBrief(b => ({ ...b, custom_note: e.target.value }))}
              style={{ ...inputStyle, margin: '4px 0' }} />
          )}
          {meta?.format?.has_branching && (
            <label style={{ display: 'block', fontSize: '13px', margin: '6px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!brief.interactive}
                onChange={e => setBrief(b => ({ ...b, interactive: e.target.checked }))} />{' '}
              Interactive/branching build — qualification questions route visitors to assets ({meta.format.format_name})
            </label>
          )}
          <div style={{ borderTop: `1px solid ${HUES.strategy.border}`, margin: '8px 0 6px', paddingTop: '6px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600 }}>Qualification / BANT questions</div>
            {QUAL_OPTIONS.map(([v, label]) => (
              <label key={v} style={{ display: 'block', fontSize: '12px', margin: '2px 0', cursor: 'pointer' }}>
                <input type="radio" name="ciq-wizard-qual" checked={qual.mode === v}
                  onChange={() => setQual(q => ({ ...q, mode: v }))} /> {label}
              </label>
            ))}
            {QUAL_TEXT_MODES.includes(qual.mode) && (
              <textarea value={qual.text} rows={3}
                onChange={e => setQual(q => ({ ...q, text: e.target.value }))}
                placeholder="The client's questions, exactly as they gave them (prefilled from the Questions step)"
                style={{ ...inputStyle, resize: 'vertical', marginTop: '4px' }} />
            )}
          </div>
          </>
          )}
          <input value={brief.notes || ''} placeholder="Anything else to constrain this build (optional)"
            onChange={e => setBrief(b => ({ ...b, notes: e.target.value }))}
            style={{ ...inputStyle, margin: '6px 0' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={generate}
              style={buttonPrimarySmall(HUES.strategy.main)}>
              Generate with this plan
            </button>
            <button onClick={() => setWizardOpen(false)} style={buttonSmall}>Cancel</button>
          </div>
        </div>
      )}

      {view === 'pages' && (
        <PagePlanEditor project={project} plan={meta?.page_plan} items={strategy} onSaved={onReload} />
      )}

      {generating && <GenerationProgress messages={STRATEGY_PROGRESS_MSGS} />}
      {msg && <div style={{ color: STATE.danger.text, fontSize: '12px', marginBottom: '6px' }}>{msg}</div>}

      {view === 'doc' && (
        <>
          {(strategy.length > 0 || flow) && (
            <div style={{ marginBottom: '8px' }}>
              <button onClick={() => downloadFileBlob(
                `${API}/contentiq/projects/${project.id}/strategy/docx`,
                `${project.name} - Content Strategy.docx`)}
                style={buttonSmall}>
                ⬇ Download .docx
              </button>
              <span style={{ color: INK.muted, fontSize: '11px', marginLeft: '8px' }}>
                Rendered from the live items/plan — edits are always included.
              </span>
            </div>
          )}
          {/* PM-facing build check (Edward, 2026-08-27): the document below
              is CLIENT-facing — this line is where the PM validates that the
              build followed the spec. Computed locally, never in the doc. */}
          {flow && (
            <div style={{ fontSize: '11.5px', color: INK.muted, margin: '6px 0 8px', lineHeight: 1.5 }}>
              Build check{meta?.format?.format_name ? ` — ${meta.format.format_name} recipe` : ''}:{' '}
              {recommendedIds.size} asset{recommendedIds.size === 1 ? '' : 's'} recommended
              {meta?.brief?.asset_count ? ` of ${meta.brief.asset_count} requested` : ''}
              {meta?.brief?.branch_count ? ` · ${meta.brief.branch_count} branches` : ''}
              {' '}· {((meta?.page_plan || {}).pages || []).length} page plan entr{((meta?.page_plan || {}).pages || []).length === 1 ? 'y' : 'ies'}
              {(meta?.brief?.funnel_stages || []).length ? ` · ${meta.brief.funnel_stages.join('/')} funnel` : ''}
              {(() => {
                const qs = (flow.stages || []).flatMap(st => st.questions || []);
                const c = qs.filter(q => q.source === 'client').length;
                return qs.length ? ` · questions: ${c} client-verbatim + ${qs.length - c} DemandArc` : '';
              })()}
            </div>
          )}
          {/* Chapter include/exclude (Edward, 2026-09-23): the checkbox sits
              ON each section heading below — one saved per-project setting;
              the docx contains exactly what's checked. */}
          {strategy.length > 0 && (
            <div style={{ fontSize: '11px', color: INK.muted, margin: '2px 0 6px' }}>
              The checkbox on each section chooses what the document includes — applies to the .docx too.
            </div>
          )}
          <StrategyDocView project={project} items={strategy} flow={flow} pagePlan={meta?.page_plan}
            formFields={meta?.form_fields} optin={meta?.optin} format={meta?.format}
            lanes={meta?.brief?.lanes} sections={docSections} sectionsCtl={{ toggle: toggleSection }} />
        </>
      )}

      {view === 'items' && (strategy.length === 0 ? (
        <div style={{ color: INK.secondary, fontSize: '13px' }}>
          No strategy items yet — add the client’s assets on the Client assets step, finish the buyer map, then generate.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '26%' }}>Recommended asset</th>
                <th style={{ ...thStyle, width: '90px' }}>Type</th>
                <th style={{ ...thStyle, width: '70px' }}>Stage</th>
                <th style={{ ...thStyle, width: '24%' }}>Pain point</th>
                <th style={thStyle}>Rationale</th>
                <th style={{ ...thStyle, width: '28px' }} />
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <React.Fragment key={g.persona}>
                  <tr>
                    <td colSpan={6} style={{ ...tdStyle, background: NEUTRAL.band, fontWeight: 700, fontSize: '12px', color: HUES.strategy.text }}>
                      {g.persona}
                    </td>
                  </tr>
                  {g.items.map(s => (
                    <tr key={s.id}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>
                        <EditableCell value={s.asset_title || ''} placeholder="asset title"
                          onSave={v => saveItem(s.id, { asset_title: v.trim() || null })} />
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
                          {s.branch_key && (
                            <span style={{ background: STATE.gate.tint, color: STATE.gate.deep, borderRadius: '10px', padding: '0 8px', fontSize: '11px', fontWeight: 600 }}>
                              branch: {s.branch_key}
                            </span>
                          )}
                          {s.content_gap && (
                            <span title="Content gap — no client asset covers this; net-new content DemandArc would produce"
                              style={{ background: STATE.danger.tint, color: STATE.danger.text, borderRadius: '10px', padding: '0 8px', fontSize: '11px', fontWeight: 600 }}>
                              GAP — net-new
                            </span>
                          )}
                          {s.pm_edited && (
                            <span title="Edited by a PM — kept on regenerate" style={{ color: BRAND.link, fontSize: '11px', fontWeight: 600 }}>✎ edited</span>
                          )}
                        </div>
                        {s.qualification_question?.question && (
                          <div style={{ color: INK.secondary, fontSize: '11px', fontWeight: 400, marginTop: '2px' }}>
                            Q: {s.qualification_question.question}
                            {(s.qualification_question.answers || []).length > 0 && ` — ${(s.qualification_question.answers || []).join(' / ')}`}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <EditableCell value={s.asset_type || ''} placeholder="type"
                          onSave={v => saveItem(s.id, { asset_type: v.trim() || null })} />
                      </td>
                      <td style={tdStyle}>
                        <EditableCell type="select" value={s.funnel_stage || 'Early'} options={FUNNEL_OPTIONS}
                          onSave={v => saveItem(s.id, { funnel_stage: v })} />
                      </td>
                      <td style={tdStyle}>
                        <EditableCell value={s.pain_point || ''} placeholder="pain point"
                          onSave={v => saveItem(s.id, { pain_point: v.trim() || null })} />
                      </td>
                      <td style={{ ...tdStyle, color: INK.body }}>
                        <EditableCell type="textarea" rows={2} value={s.rationale || ''} placeholder="rationale"
                          onSave={v => saveItem(s.id, { rationale: v.trim() || null })} />
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => removeItem(s.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK.muted }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function PagePlanEditor({ project, plan, items, onSaved }) {
  // The PM's deliverable plan: merge/split/reassign the generated pages
  // freely — this is what the client approves and what the LP copy
  // generator will consume, one hand-off doc per row.
  const [pages, setPages] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => { setPages((plan?.pages || []).map(p => ({ ...p }))); setDirty(false); }, [plan]);

  const assets = [];
  items.forEach(s => {
    if (s.asset_id && !assets.some(a => a.id === s.asset_id)) assets.push({ id: s.asset_id, title: s.asset_title });
  });

  const edit = (i, field, value) => {
    setPages(ps => ps.map((p, pi) => (pi === i ? { ...p, [field]: value } : p)));
    setDirty(true);
  };
  const toggleAsset = (i, assetId) => {
    setPages(ps => ps.map((p, pi) => {
      if (pi !== i) return p;
      const has = (p.asset_ids || []).includes(assetId);
      return { ...p, asset_ids: has ? p.asset_ids.filter(x => x !== assetId) : [...(p.asset_ids || []), assetId] };
    }));
    setDirty(true);
  };
  const addPage = () => { setPages(ps => [...ps, { name: `Landing page ${ps.length + 1}`, audience: '', stages: '', asset_ids: [], notes: '' }]); setDirty(true); };
  const removePage = (i) => { setPages(ps => ps.filter((_, pi) => pi !== i)); setDirty(true); };
  const save = async () => {
    setSaving(true); setNote('');
    try {
      await axios.put(`${API}/contentiq/projects/${project.id}/strategy/page-plan`, { pages });
      setDirty(false); setNote('Saved.');
      setTimeout(() => setNote(''), 2000);
      onSaved();
    } catch (e) { setNote(e.response?.data?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };

  if (!plan && pages.length === 0) {
    return <div style={{ color: INK.secondary, fontSize: '13px' }}>No page plan yet — generate the strategy first, then shape the pages here.</div>;
  }
  return (
    <div>
      <div style={{ color: INK.secondary, fontSize: '12px', marginBottom: '8px' }}>
        Each row is one landing page to build. Merge, split, and reassign freely — the page
        copy follows this plan, one document per page.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '22%' }}>Page</th>
              <th style={{ ...thStyle, width: '20%' }}>Audience</th>
              <th style={{ ...thStyle, width: '12%' }}>Stages</th>
              <th style={{ ...thStyle, width: '24%' }}>Assets featured</th>
              <th style={thStyle}>Notes</th>
              <th style={{ ...thStyle, width: '28px' }} />
            </tr>
          </thead>
          <tbody>
            {pages.map((p, i) => (
              <tr key={i}>
                <td style={tdStyle}><input value={p.name || ''} onChange={e => edit(i, 'name', e.target.value)} style={inputStyle} /></td>
                <td style={tdStyle}><input value={p.audience || ''} onChange={e => edit(i, 'audience', e.target.value)} style={inputStyle} /></td>
                <td style={tdStyle}><input value={p.stages || ''} onChange={e => edit(i, 'stages', e.target.value)} style={inputStyle} /></td>
                <td style={tdStyle}>
                  {assets.length === 0 && <span style={{ color: INK.muted, fontSize: '12px' }}>—</span>}
                  {assets.map(a => (
                    <label key={a.id} style={{ display: 'block', fontSize: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={(p.asset_ids || []).includes(a.id)}
                        onChange={() => toggleAsset(i, a.id)} /> {a.title}
                    </label>
                  ))}
                </td>
                <td style={tdStyle}><input value={p.notes || ''} onChange={e => edit(i, 'notes', e.target.value)} style={inputStyle} /></td>
                <td style={tdStyle}>
                  <button onClick={() => removePage(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK.muted }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
        <button onClick={addPage} style={buttonSmall}>+ Add page</button>
        <button onClick={save} disabled={saving || !dirty}
          style={dirty && !saving
            ? buttonPrimarySmall(HUES.strategy.main)
            : { ...buttonPrimarySmall(), ...buttonDisabled }}>
          {saving ? 'Saving…' : 'Save plan'}
        </button>
        {note && <span style={{ fontSize: '12px', color: note === 'Saved.' ? STATE.done.text : STATE.danger.text }}>{note}</span>}
      </div>
    </div>
  );
}

function StrategyDocView({ project, items, flow, pagePlan, sections, sectionsCtl, formFields, optin, format, lanes }) {
  // The strategy document, rendered in the shape of DemandArc's real strategy
  // docs (Edward's sent Indeed SMB doc, 2026-09-17): overview + asset map ->
  // structure table -> recommended assets -> planned pages -> per-stage copy
  // and qualification questions with routing recap. `sections` mirrors the
  // docx's chapter selection exactly — a missing key means included.
  const on = (k) => !sections || sections[k] !== false;
  // Inline include/exclude (Edward, 2026-09-23: the checkbox belongs NEXT TO
  // the section it controls). With sectionsCtl, every section heading carries
  // its own checkbox; an excluded section stays visible as a dimmed stub in
  // the app (so it can be re-included) and drops fully from the docx.
  const SecHead = ({ k, children, style }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px', ...style }}>
      {sectionsCtl && (
        <input type="checkbox" checked={on(k)} onChange={() => sectionsCtl.toggle(k)}
          title="Include this section in the document (and the .docx)" style={{ cursor: 'pointer' }} />
      )}
      <span style={{ fontWeight: 700, opacity: on(k) ? 1 : 0.45 }}>{children}</span>
      {!on(k) && (
        <span style={{ fontSize: '11px', color: INK.muted, fontStyle: 'italic' }}>— excluded from the document</span>
      )}
    </div>
  );
  const titleById = {};
  items.forEach(s => { if (s.asset_id) titleById[s.asset_id] = s.asset_title; });
  // Disqualify marker printed exactly ONCE: a marker embedded in the answer
  // text (client verbatim) must not double the structured flag's own
  // (Edward, 2026-09-23 — mirrors the backend's strip_disqualify_suffix).
  const DQ_SUFFIX = /\s*(\[\s*disqualify\s*\]|[—–-]{1,2}\s*(please\s+)?disqualify)\s*$/i;
  const answerText = (a) => String(a.text || '').replace(DQ_SUFFIX, '').trimEnd();
  const answerRoute = (a) => {
    if (a.disqualify || DQ_SUFFIX.test(String(a.text || ''))) return ' [Disqualify]';
    if (a.routes_to_asset_id && titleById[a.routes_to_asset_id]) return ` → ${titleById[a.routes_to_asset_id]}`;
    return '';
  };
  // House provenance: the model's own source_note when present (names the
  // exact source set/document), else the older binary client/DA labels.
  const srcTag = (entry, clientLabel, daLabel) => {
    const note = (entry.source_note || '').trim();
    if (note) return `[${note}]`;
    if (entry.source) return entry.source === 'client' ? clientLabel : daLabel;
    return null;
  };
  const srcColor = (entry) => (entry.source === 'client' ? STATE.done.text : INK.muted);
  const assetMap = (flow?.asset_map || []).filter(r => r && typeof r === 'object');
  const flowStages = (flow?.stages || []).filter(st => st && typeof st === 'object');
  const recipeFlow = flowStages.some(st => st.kind);
  const twoTouch = isTwoTouchFormat(format);
  // Kinds that are their own Engagement Phase, never a page (phone since B2;
  // email joined with 2-Touch CS's second touch, recipe round 2).
  const OWN_PHASE_KINDS = ['phone', 'email'];
  const ownPhase = (k) => OWN_PHASE_KINDS.includes(k);
  const stepLabel = (st) => {
    // B2 vocabulary (Edward, 2026-10-19): "Stage" never heads a recipe
    // flow's entries — pages sit in Engagement Phase 1, calls/emails are
    // later Engagement Phases.
    if (!recipeFlow) return `Stage ${st.stage}`;
    if (ownPhase(st.kind)) {
      const own = flowStages.filter(x => ownPhase(x.kind));
      const hasPages = flowStages.some(x => !ownPhase(x.kind));
      return `Engagement Phase ${own.indexOf(st) + (hasPages ? 2 : 1)}`;
    }
    // Every recipe's pages name their phase (Edward, 2026-10-28: a Deal
    // Accelerator's phase 1 is several pages, its script phase 2) — mirrors
    // the docx's _step_label.
    const pages = flowStages.filter(x => !ownPhase(x.kind));
    return `Engagement Phase 1 — Page ${pages.indexOf(st) + 1}`;
  };
  // The copy chapter's Engagement Phase groups — mirrors the docx's
  // _phase_groups exactly (same labels, same Demand Cascade tags).
  const phaseGroups = (() => {
    if (!recipeFlow) return null;
    const pages = flowStages.filter(s => !ownPhase(s.kind));
    const phones = flowStages.filter(s => ownPhase(s.kind));
    const tps = (format?.strategy_recipe?.touchpoints || []).filter(t => t && typeof t === 'object');
    const tpPages = tps.filter(t => !ownPhase(t.kind));
    const tpPhones = tps.filter(t => ownPhase(t.kind));
    const shares = format?.strategy_recipe?.lead_shares || {};
    const tagOf = (tp) => {
      const n = tp?.program_stage;
      if (!n) return null;
      let t = `${format?.format_name ? `${format.format_name} ` : ''}Stage ${n}`;
      const share = shares[String(n)];
      if (typeof share === 'number' && share > 0 && share <= 1) t += ` · ${Math.round(share * 100)}% of leads`;
      return t;
    };
    const groups = [];
    if (pages.length) groups.push({ label: 'Engagement Phase 1 — Landing pages', tag: tagOf(tpPages[0]), kind: 'pages', stages: pages });
    phones.forEach((s, i) => groups.push({
      label: `Engagement Phase ${(pages.length ? 2 : 1) + i} — ${s.page || (s.kind === 'email' ? 'Email' : 'Call')}`,
      tag: tagOf(tpPhones[i]), kind: s.kind || 'phone', stages: [s],
    }));
    return groups;
  })();
  if (!items.length) {
    return <div style={{ color: INK.secondary, fontSize: '13px' }}>Nothing to render yet — generate the strategy first.</div>;
  }
  return (
    <div style={{ background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.tile, boxShadow: SHADOW.card, padding: '18px 22px', fontSize: '13px', maxWidth: '820px' }}>
      <div style={displayFont('18px', 700)}>{project.name} — Content Strategy</div>
      {(flow?.overview || assetMap.length > 0) && (
        <SecHead k="overview" style={{ marginTop: '12px' }}>Content Strategy Overview</SecHead>
      )}
      {flow?.overview && on('overview') && (
        <p style={{ margin: '4px 0' }}>{flow.overview}</p>
      )}
      {(flow?.stages || []).length > 0 && sectionsCtl && (
        <SecHead k="structure" style={{ marginTop: '10px', fontSize: '12px' }}>Flow table</SecHead>
      )}
      {(flow?.stages || []).length > 0 && on('structure') && (
        <>
        {/* Two separate tables (Edward, 2026-10-19): the flow table lives in
            the overview with its lead-in; the asset/segment table lives in
            Recommended Assets & Audiences below. */}
        <div style={{ margin: '6px 0 2px' }}>The stages &amp; flow are as follows:</div>
        <table style={{ borderCollapse: 'collapse', margin: '4px 0 8px', fontSize: '12px' }}>
          <thead>
            <tr>{(recipeFlow ? ['Step', 'Touchpoint', 'Purpose'] : ['Stage', 'Page', 'Purpose']).map(h => (
              <th key={h} style={{ ...thStyle, padding: '4px 10px' }}>{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {flow.stages.map(st => (
              <tr key={st.stage}>
                <td style={{ ...tdStyle, padding: '4px 10px' }}>{recipeFlow ? stepLabel(st) : st.stage}</td>
                <td style={{ ...tdStyle, padding: '4px 10px' }}>{st.page}</td>
                <td style={{ ...tdStyle, padding: '4px 10px' }}>{st.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}

      <SecHead k="assets" style={{ marginTop: '12px' }}>Recommended Assets &amp; Audiences</SecHead>
      {on('assets') && twoTouch && assetPairs(pagePlan, items).length > 0 && (() => {
        // Two-touch (Edward, 2026-10-28): the recommendation reads as PAIRS
        // — one row per pair, each pair's assets grouped under it.
        const pairs = assetPairs(pagePlan, items);
        const paired = new Set(pairs.flatMap(pr => pr.members.map(m => m.assetId)));
        const gaps = items.filter(s => s.content_gap && !paired.has(s.asset_id));
        return (
          <>
            <table style={{ borderCollapse: 'collapse', margin: '4px 0 8px', fontSize: '12px' }}>
              <thead>
                <tr>{['Pair', 'Engagement Phase 1 asset', 'Engagement Phase 2 asset'].map(h => (
                  <th key={h} style={{ ...thStyle, padding: '4px 10px' }}>{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {pairs.map((pr, i) => (
                  <tr key={i}>
                    <td style={{ ...tdStyle, padding: '4px 10px' }}>{pr.name}</td>
                    {[1, 2].map(n => {
                      const m = pr.members.find(x => x.phase === n);
                      return (
                        <td key={n} style={{ ...tdStyle, padding: '4px 10px' }}>
                          {m ? (titleById[m.assetId] || `asset ${m.assetId}`) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {pairs.map((pr, i) => (
              <div key={i} style={{ margin: '8px 0' }}>
                <div style={{ fontWeight: 700 }}>{pr.name}</div>
                {pr.members.map(m => (
                  <div key={m.assetId} style={{ margin: '4px 0 4px 8px' }}>
                    <strong>Engagement Phase {m.phase}: {titleById[m.assetId] || `asset ${m.assetId}`}</strong>
                    {m.item?.asset_type && <span style={{ color: INK.secondary }}> ({m.item.asset_type})</span>}
                    {m.item && (
                      <div style={{ color: INK.body }}>
                        {m.item.persona_name}{m.item.funnel_stage ? ` · ${m.item.funnel_stage}` : ''}
                        {m.item.pain_point ? ` — ${m.item.pain_point}` : ''}
                      </div>
                    )}
                    {m.item?.rationale && <div style={{ color: INK.secondary, fontSize: '12px' }}>{m.item.rationale}</div>}
                  </div>
                ))}
              </div>
            ))}
            {gaps.map(s => (
              <div key={s.id} style={{ margin: '6px 0' }}>
                <strong>{s.asset_title}</strong>
                <span style={{ color: STATE.danger.text, fontWeight: 600 }}> — content gap: net-new content DemandArc would produce</span>
                {s.rationale && <div style={{ color: INK.secondary, fontSize: '12px' }}>{s.rationale}</div>}
              </div>
            ))}
          </>
        );
      })()}
      {on('assets') && !(twoTouch && assetPairs(pagePlan, items).length > 0) && assetMap.length > 0 && (
        <table style={{ borderCollapse: 'collapse', margin: '4px 0 8px', fontSize: '12px' }}>
          <thead>
            <tr>{['Segment', 'Recommended Content Asset', 'Target Personas'].map(h => (
              <th key={h} style={{ ...thStyle, padding: '4px 10px' }}>{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {assetMap.map((r, i) => (
              <tr key={i}>
                <td style={{ ...tdStyle, padding: '4px 10px' }}>{r.segment || r.product}</td>
                <td style={{ ...tdStyle, padding: '4px 10px' }}>{r.asset_title}</td>
                <td style={{ ...tdStyle, padding: '4px 10px' }}>{r.personas || r.product}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {on('assets') && !(twoTouch && assetPairs(pagePlan, items).length > 0) && (() => {
        // The document reflects the narrowed build (Edward, round 3): only
        // assets featured in the page plan, plus content gaps as their own
        // callout. The full analysis stays in the Items tab + Stage 2 matrix.
        const featured = new Set((pagePlan?.pages || []).flatMap(p => p.asset_ids || []));
        const docItems = featured.size
          ? items.filter(s => (s.asset_id && featured.has(s.asset_id)) || s.content_gap)
          : items;
        const hidden = items.length - docItems.length;
        return (
          <>
            {docItems.map(s => (
        <div key={s.id} style={{ margin: '6px 0' }}>
          <strong>{s.asset_title}</strong>
          {s.asset_type && <span style={{ color: INK.secondary }}> ({s.asset_type})</span>}
          {s.content_gap && <span style={{ color: STATE.danger.text, fontWeight: 600 }}> — content gap: net-new content DemandArc would produce</span>}
          <div style={{ color: INK.body }}>
            {s.persona_name}{s.funnel_stage ? ` · ${s.funnel_stage}` : ''}{s.pain_point ? ` — ${s.pain_point}` : ''}
          </div>
          {s.rationale && <div style={{ color: INK.secondary, fontSize: '12px' }}>{s.rationale}</div>}
        </div>
            ))}
            {hidden > 0 && (
              <div style={{ color: INK.muted, fontSize: '12px', marginTop: '4px' }}>
                {hidden} further analyzed asset–persona pairing{hidden === 1 ? '' : 's'} not featured in this build —
                see the Items tab (and the Buyer map) for the full analysis.
              </div>
            )}
          </>
        );
      })()}

      {(pagePlan?.pages || []).length > 0 && (
        <SecHead k="pages" style={{ marginTop: '12px' }}>Planned Landing Pages</SecHead>
      )}
      {(pagePlan?.pages || []).length > 0 && on('pages') && (
        <>
          {(pagePlan.pages || []).map((p, i) => (
            <div key={i} style={{ margin: '6px 0' }}>
              <strong>{p.name}</strong>
              <span style={{ color: INK.secondary }}> — {p.audience}{p.stages ? ` · ${p.stages}` : ''}</span>
              {(p.asset_ids || []).length > 0 && (() => {
                const feats = (p.asset_ids || []).map(id => {
                  const it = items.find(s => s.asset_id === id);
                  return it ? it.asset_title : `asset ${id}`;
                });
                // Two-touch: the landing page features the pair's phase-1
                // asset; the phase-2 asset goes out in the follow-up email.
                return twoTouch ? (
                  <div style={{ color: INK.body, fontSize: '12px' }}>
                    Features: {feats[0]}
                    {feats.length > 1 && <div>Follow-up email (Engagement Phase 2): {feats[1]}</div>}
                  </div>
                ) : (
                  <div style={{ color: INK.body, fontSize: '12px' }}>Features: {feats.join(' · ')}</div>
                );
              })()}
              {p.notes && <div style={{ color: INK.secondary, fontSize: '12px' }}>{p.notes}</div>}
            </div>
          ))}
        </>
      )}

      {(() => {
        // Heading (with its checkbox) renders whenever the chapter has
        // content, even excluded — the stub is how you re-include it.
        const dc = (flow?.draft_copy || []).filter(e => e && typeof e === 'object');
        const any = (flow?.stages || []).some(st => (st?.questions || []).length > 0
            || (st?.script_lines || []).length > 0 || st?.standard_firmographics)
          || dc.some(e => (e.stages || []).some(st => (st?.blocks || []).length));
        return any ? (
          <SecHead k="copy" style={{ marginTop: '14px', fontSize: '14px' }}>
            Recommended Copy &amp; Qualification Questions
          </SecHead>
        ) : null;
      })()}
      {on('copy') && (() => {
        // House format (the delivered Indeed/SailPoint docs): one chapter,
        // then per-stage sections — DRAFT COPY blocks first (the strategy
        // doc IS the proposed copy), that stage's questions after. A stage
        // with neither (the asset-delivery page) still gets its section in
        // the house phrasing — the asset itself IS that page.
        const draftCopy = (flow?.draft_copy || []).filter(e => e && typeof e === 'object');
        const copyFor = (stageNo, onlyEntry) => (onlyEntry ? [onlyEntry] : draftCopy).flatMap(e =>
          (e.stages || []).filter(st => st.stage === stageNo && (st.blocks || []).length)
            .map(st => ({ pageName: e.page_name, blocks: st.blocks })));
        // Lane-major (R2.4, Edward's approved FINAL Indeed DA): with a lane
        // plan, each lane's full page set renders together, registration
        // details printed once. firmoShown is per render pass.
        const laneMajor = (lanes || []).length > 0 && draftCopy.length > 1;
        const firmoShown = { v: false };
        const allStages = (flow?.stages || []).filter(st => st && typeof st === 'object');
        const hasContent = allStages.some(st =>
          (st.questions || []).length > 0 || copyFor(st.stage).length > 0
          || (st.script_lines || []).length > 0 || st.standard_firmographics);
        if (!hasContent) return null;
        // Same narrowing as the Recommended Assets chapter and the docx:
        // the asset-delivery house line names the BUILD's assets, never the
        // full analyzed list (Edward's staging screenshot named an asset the
        // build didn't even feature).
        const featured = new Set((pagePlan?.pages || []).flatMap(p => p.asset_ids || []));
        const narrowed = featured.size ? items.filter(s => s.asset_id && featured.has(s.asset_id)) : items;
        const featuredTitles = [...new Set(narrowed
          .filter(s => s.asset_id && titleById[s.asset_id]).map(s => titleById[s.asset_id]))];
        const renderStage = (st, headingLabel, onlyEntry) => {
              const stageCopy = copyFor(st.stage, onlyEntry);
              const laneName = String(onlyEntry?.page_name || '').trim();
              let stageQs = st.questions || [];
              if (onlyEntry) {
                // Lane-major: shared (unlabeled) questions show in every
                // lane; one labeled with ANOTHER lane's name is that lane's.
                // This lane's own name never counts as "other" — duplicate
                // lane names degrade toward showing, never hide a question
                // from every lane.
                const laneSelf = laneName.toLowerCase();
                const others = draftCopy.filter(e => e !== onlyEntry)
                  .map(e => String(e.page_name || '').trim().toLowerCase())
                  .filter(o => o && o !== laneSelf);
                stageQs = stageQs.filter(q => !others.some(o => String(q.label || '').toLowerCase().startsWith(o)));
              }
              const scriptLines = st.script_lines || [];
              // Lane-major prints the registration block once; later lanes
              // get a one-line pointer (mirrors the docx).
              const firmoRepeat = !!(st.standard_firmographics && onlyEntry && firmoShown.v);
              if (st.standard_firmographics && !firmoRepeat) firmoShown.v = true;
              if (!stageCopy.length && !stageQs.length && !scriptLines.length && !st.standard_firmographics) {
                // Lane-major: the delivery page names THAT lane's asset(s)
                // (its page_plan entry is named for the lane), never the
                // whole build's.
                const lanePg = laneName
                  ? (pagePlan?.pages || []).find(p => String(p.name || '').trim().toLowerCase() === laneName.toLowerCase())
                  : null;
                const laneTitles = lanePg
                  ? [...new Set((lanePg.asset_ids || []).map(a => titleById[a] || `asset ${a}`))]
                  : featuredTitles;
                return (
                  <div>
                    {headingLabel && <div style={{ fontWeight: 700, marginTop: '12px' }}>{headingLabel}</div>}
                    <div style={{ margin: '4px 0 4px 8px' }}>Qualified users are presented with the content asset.</div>
                    {laneTitles.length > 0 && (
                      <div style={{ margin: '0 0 4px 8px', color: INK.body }}>
                        Asset opened in browser: {laneTitles.join(' · ')}
                      </div>
                    )}
                  </div>
                );
              }
              return (
              <div>
                {headingLabel && <div style={{ fontWeight: 700, marginTop: '12px' }}>{headingLabel}</div>}
                {stageCopy.map((c, ci) => (
                  <div key={ci} style={{ margin: '6px 0 6px 8px' }}>
                    {!onlyEntry && draftCopy.length > 1 && c.pageName && (
                      <div style={{ fontWeight: 700, fontSize: '12.5px' }}>{c.pageName}</div>
                    )}
                    {(c.blocks || []).map((b, bi) => (
                      <div key={bi} style={{ margin: '4px 0' }}>
                        {(b.label || 'Copy') !== ((c.blocks[bi - 1] || {}).label || (bi === 0 ? null : 'Copy')) && (
                          <>
                            <span style={{ fontWeight: 600, fontSize: '12px', color: HUES.lp.text }}>{b.label || 'Copy'}</span>
                            {srcTag(b, '[Client copy — verbatim]', '[DemandArc draft]') && (
                              <span style={{ fontSize: '11px', fontStyle: 'italic', color: srcColor(b), marginLeft: '6px' }}>
                                {srcTag(b, '[Client copy — verbatim]', '[DemandArc draft]')}
                              </span>
                            )}
                          </>
                        )}
                        {bulletLines(b.label, b.text) ? (
                          <ul style={{ margin: '2px 0', paddingLeft: '20px', color: INK.body }}>
                            {bulletLines(b.label, b.text).map((line, li) => <li key={li}>{line}</li>)}
                          </ul>
                        ) : (
                          <div style={{ whiteSpace: 'pre-wrap', color: INK.body }}>{b.text}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                {firmoRepeat && (
                  <div style={{ margin: '6px 0 2px 8px', fontSize: '11.5px', fontStyle: 'italic', color: INK.muted }}>
                    Registration form fields and opt-in are identical across lanes — see the first lane above.
                  </div>
                )}
                {st.standard_firmographics && !firmoRepeat && (
                  (formFields || []).length > 12 ? (
                    // Full-lead-template programs (Edward's 2026-10-19 Indeed
                    // review): the doc summarizes instead of dumping 40 fields.
                    <div style={{ margin: '6px 0 2px 8px' }}>
                      <div style={{ fontWeight: 600 }}>Registration form fields</div>
                      <div>Standard firmographic fields (e.g., name, title, company, industry, etc.)</div>
                      <div style={{ fontSize: '11.5px', fontStyle: 'italic', color: INK.muted }}>
                        Full lead template — {formFields.length} fields — tracked in NeTrA (QA Rules → Required Fields).
                      </div>
                    </div>
                  ) : (formFields || []).length > 0 ? (
                    <div style={{ margin: '6px 0 2px 8px' }}>
                      <div style={{ fontWeight: 600 }}>Registration form fields</div>
                      <ul style={{ margin: '2px 0', paddingLeft: '20px' }}>
                        {formFields.map((f, fi) => (
                          <li key={fi}>
                            {f.client_alias || f.label}
                            {f.tier === 'optional' && <span style={{ color: INK.muted }}> (optional)</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div style={{ margin: '6px 0 2px 8px' }}>
                      <div style={{ fontWeight: 600 }}>
                        Standard firmographic fields (e.g., name, title, company, industry, etc.)
                      </div>
                      {sectionsCtl && (
                        <div style={{ fontSize: '11.5px', fontStyle: 'italic', color: INK.muted }}>
                          Shown because this program has no form fields set — set the client’s own in{' '}
                          <a href="/qa-rules" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.link }}>
                            QA Rules → Required Fields
                          </a>. (This note stays out of the document.)
                        </div>
                      )}
                    </div>
                  )
                )}
                {st.standard_firmographics && !firmoRepeat && optin && (
                  <div style={{ margin: '6px 0 2px 8px' }}>
                    <span style={{ fontWeight: 600 }}>Opt-in / privacy</span>
                    {optin.optin_language ? (
                      <>
                        <span style={{ fontSize: '11px', fontStyle: 'italic', color: STATE.done.text, marginLeft: '6px' }}>
                          [Client-supplied — verbatim]
                        </span>
                        <div style={{ whiteSpace: 'pre-wrap', color: INK.body }}>{optin.optin_language}</div>
                      </>
                    ) : optin.optin_proposed ? (
                      <>
                        <span style={{ fontSize: '11px', fontStyle: 'italic', color: STATE.gate.text, marginLeft: '6px' }}>
                          [Proposed — for the client's approval]
                        </span>
                        <div style={{ whiteSpace: 'pre-wrap', color: INK.body }}>{optin.optin_proposed}</div>
                      </>
                    ) : (
                      <div style={{ color: INK.secondary }}>
                        Opt-in language to be proposed for the client's approval — the
                        registration form requires consent (initially unchecked) with the
                        privacy policy linked.
                      </div>
                    )}
                    {(optin.privacy_links || []).length > 0 ? (
                      <ul style={{ margin: '2px 0', paddingLeft: '20px' }}>
                        {optin.privacy_links.map((l, li) => (
                          <li key={li}>{l.label ? `${l.label}: ` : ''}{l.url}</li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{ color: STATE.danger.text, fontWeight: 700 }}>
                        ⚠ Privacy policy link required — add the client's privacy policy URL
                        on the Questions &amp; privacy step.
                      </div>
                    )}
                  </div>
                )}
                {scriptLines.length > 0 && (
                  <div style={{ margin: '6px 0 6px 8px' }}>
                    {scriptLines.map((line, li) => {
                      const text = String(line || '').trim();
                      if (!text) return null;
                      const directive = text.startsWith('[') && text.endsWith(']');
                      return (
                        <div key={li} style={directive
                          ? { fontWeight: 700, fontStyle: 'italic', fontSize: '12px', color: INK.secondary, margin: '3px 0' }
                          : { color: INK.body, margin: '3px 0' }}>
                          {text}
                        </div>
                      );
                    })}
                  </div>
                )}
                {stageQs.map((q, qi) => (
                  <div key={qi} style={{ margin: '6px 0 6px 8px' }}>
                    {q.label && <span style={{ fontWeight: 600 }}>{q.label} — </span>}{q.question}
                    {srcTag(q, '[Client-supplied — verbatim]', '[DemandArc]') && (
                      <span style={{ fontSize: '11px', fontStyle: 'italic', color: srcColor(q), marginLeft: '6px' }}>
                        {srcTag(q, '[Client-supplied — verbatim]', '[DemandArc]')}
                      </span>
                    )}
                    <ul style={{ margin: '2px 0', paddingLeft: '20px' }}>
                      {(q.answers || []).map((a, ai) => (
                        <li key={ai}>
                          {answerText(a)}
                          <span style={{ color: a.disqualify ? STATE.danger.text : STATE.done.text, fontWeight: 600 }}>{answerRoute(a)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              );
        };
        const phaseHeader = (grp) => (
          <div style={{ marginTop: '14px', background: HUES.strategy.tint, borderRadius: RADII.small, padding: '6px 12px', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {grp.label}
            {grp.tag && (
              <span style={{ background: STATE.done.tint, color: STATE.done.deep, borderRadius: RADII.pill, padding: '1px 10px', fontSize: '10.5px', fontWeight: 600 }}>{grp.tag}</span>
            )}
          </div>
        );
        return (
          <>
            {phaseGroups ? phaseGroups.map((grp, gi) => (
              <div key={gi}>
                {phaseHeader(grp)}
                {String(grp.stages[0]?.narrative || '').trim() && (
                  <p style={{ margin: '6px 0 2px' }}>{grp.stages[0].narrative}</p>
                )}
                {grp.kind === 'pages' && laneMajor
                  ? draftCopy.map((entry, ei) => (
                      <div key={ei}>
                        {String(entry.page_name || '').trim() && (
                          <div style={{ fontWeight: 700, fontSize: '13px', marginTop: '10px', color: HUES.strategy.text }}>
                            {entry.page_name}
                          </div>
                        )}
                        {grp.stages.map((st, i) => (
                          <div key={st.stage ?? i}>{renderStage(st, `Page ${i + 1}: ${st.page || ''}`, entry)}</div>
                        ))}
                      </div>
                    ))
                  : grp.kind === 'pages'
                  ? grp.stages.map((st, i) => (
                      <div key={st.stage ?? i}>{renderStage(st, `Page ${i + 1}: ${st.page || ''}`)}</div>
                    ))
                  : renderStage(grp.stages[0], null)}
              </div>
            )) : allStages.map(st => (
              <div key={st.stage}>{renderStage(st, `${stepLabel(st)}: ${st.page || ''}`)}</div>
            ))}
          </>
        );
      })()}
      {(() => {
        // Routing recap — mirrors the docx's closing section exactly. The
        // heading (with its checkbox) survives exclusion; the list doesn't.
        const routed = (flow?.stages || []).flatMap(st => (st.questions || []).flatMap(q =>
          (q.answers || []).filter(a => a && a.routes_to_asset_id && titleById[a.routes_to_asset_id])
            .map(a => ({ text: a.text, title: titleById[a.routes_to_asset_id] }))));
        if (!routed.length) return null;
        return (
          <>
            <SecHead k="routing" style={{ marginTop: '12px' }}>
              Routing recap — from the challenge question on the landing page
            </SecHead>
            {on('routing') && (
              <ul style={{ margin: '2px 0', paddingLeft: '20px' }}>
                {routed.map((r, i) => (
                  <li key={i}>{r.text} → User gets asset: {r.title}</li>
                ))}
              </ul>
            )}
          </>
        );
      })()}
      {!flow && (
        <div style={{ color: STATE.gate.text, fontSize: '12px', marginTop: '10px' }}>
          This strategy predates the question/flow spec — regenerate to produce the full document.
        </div>
      )}
      {flow?.generated_at && (
        <div style={{ color: INK.muted, fontSize: '11px', marginTop: '12px' }}>
          Generated {new Date(flow.generated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

export function Stage4Panel({ project, strategy, meta, onStageChanged, section = 'all',
                            embedded = false, autoCreate = false, onCopyChanged, copyFromAssets = false }) {
  // The standalone Tele-Confirmation Scripts half retired in R2.3: scripts
  // are sections OF the strategy document (phone touchpoints' script_lines),
  // never a separate deliverable. This panel is the landing-page copy
  // hand-off: exact copy, one deliverable per page-plan row.
  // `embedded` (Edward, 2026-10-22): Final copy is no longer its own step —
  // the copy lives inside Page build as "what the build team receives", so
  // the strategy download and the stage-4 status control drop out, and
  // `autoCreate` extracts it once, unprompted, when the approved strategy
  // has no copy yet.
  const [lpCopy, setLpCopy] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState('');
  const autoTried = useRef(false);

  const loadCopy = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contentiq/projects/${project.id}/outputs/lp-copy`);
      setLpCopy(res.data);
    } catch (e) { setLpCopy({ pages: [] }); }
  }, [project.id]);
  useEffect(() => { loadCopy(); }, [loadCopy]);

  const stage = (project.stages || []).find(st => st.stage_number === 4);
  const approved = true;  // approval gate removed (Edward, 2026-08-27)
  const planPages = meta?.page_plan?.pages || [];
  const pages = lpCopy?.pages || [];
  // The earlier page set holding the identical page, if any (same page name
  // and the same words) — how a shared interactive page shows once.
  const blockKey = (st) => JSON.stringify((st.blocks || []).map(b => [b.label, b.text]));
  const sharedEarlier = (i, st) => {
    for (let k = 0; k < i; k += 1) {
      const twin = ((pages[k] || {}).stages || []).find(x => x.page === st.page && blockKey(x) === blockKey(st));
      if (twin && (st.blocks || []).length) return pages[k].page_name;
    }
    return null;
  };
  // The skip-to-pages path: pages ASSEMBLED verbatim from client copy, no
  // strategy, no model. Refreshing them must re-run the assembly — the
  // generate endpoint is for strategy projects and the backend now refuses
  // it here (staging round 3: "Re-extract" regenerated over verbatim copy).
  const assembled = !!lpCopy?.assembled;
  const assembleAssetIds = [...new Set(planPages.flatMap(p => p.asset_ids || []))];

  const generate = async () => {
    if (pages.length > 0 && !window.suppressConfirm &&
        !window.confirm('Regenerate the landing-page copy? The current copy is replaced.')) return;
    setGenerating(true); setMsg('');
    try {
      const res = await axios.post(`${API}/contentiq/projects/${project.id}/outputs/lp-copy/generate`,
        null, { timeout: 240000 });
      setLpCopy(res.data);
      if (onCopyChanged) await onCopyChanged();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  useEffect(() => {
    if (!autoCreate || autoTried.current || lpCopy === null || generating) return;
    if (pages.length || assembled || planPages.length === 0) return;
    autoTried.current = true;
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreate, lpCopy]);

  const reassemble = async () => {
    if (pages.length > 0 && !window.suppressConfirm &&
        !window.confirm('Re-assemble the landing pages from the client’s current copy, '
          + 'questions and opt-in/privacy language? No AI — verbatim from what’s saved in Setup.')) return;
    setGenerating(true); setMsg('');
    try {
      const res = await axios.post(`${API}/contentiq/projects/${project.id}/outputs/assemble-pages`,
        { asset_ids: assembleAssetIds });
      setLpCopy(res.data);
      if (onCopyChanged) await onCopyChanged();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Re-assembly failed.');
    } finally { setGenerating(false); }
  };

  return (
    <div style={embedded ? {} : { ...panelCard, marginBottom: '14px' }}>
      {!embedded && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <strong style={panelTitle}>Final copy</strong>
        <span style={{ marginLeft: 'auto' }} />
        <DeliverableStatus projectId={project.id} stageNumber={4} stage={stage} onChanged={onStageChanged} />
      </div>
      )}

      {!embedded && (
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Content Strategy Document</div>
        {strategy.length > 0 ? (
          <button onClick={() => downloadFileBlob(
            `${API}/contentiq/projects/${project.id}/strategy/docx`,
            `${project.name} - Content Strategy.docx`)}
            style={buttonSmall}>
            ⬇ Content Strategy (.docx)
          </button>
        ) : assembled ? (
          <span style={{ color: INK.secondary, fontSize: '13px' }}>
            No strategy — skipped on purpose: these pages are assembled verbatim from the client's copy.
          </span>
        ) : pages.length === 0 ? (
          <span style={{ color: INK.secondary, fontSize: '13px' }}>
            No strategy yet. Pages come either from the Content plan (assemble the client's
            copy verbatim — the syndication path) or from the content strategy.
          </span>
        ) : (
          <span style={{ color: INK.secondary, fontSize: '13px' }}>No strategy yet — generate it on the Content strategy step.</span>
        )}
      </div>
      )}

      <div style={embedded ? {} : { borderTop: `1px solid ${NEUTRAL.borderSoft}`, paddingTop: '10px' }}>
        {!embedded && <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Page copy</div>}
        {!approved && (
          <div style={{ background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`, color: STATE.gate.deep, borderRadius: RADII.small, padding: '8px 12px', fontSize: '13px', marginBottom: '8px' }}>
            🔒 Locked until the client approves the strategy — record their approval on the
            Content strategy step once they sign off.
          </div>
        )}
        {assembled && lpCopy?.stale && (
          <div style={{ background: STATE.gate.tint, border: `1px solid ${STATE.gate.border}`, color: STATE.gate.text, borderRadius: RADII.small, padding: '8px 12px', fontSize: '12.5px', marginBottom: '8px', lineHeight: 1.5 }}>
            ⚠ Setup has changed since these pages were assembled — re-assemble to pick up
            the latest copy, questions and opt-in/privacy language.
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
          {assembled ? (
            <>
              <button onClick={reassemble} disabled={generating || assembleAssetIds.length === 0}
                style={assembleAssetIds.length > 0 && !generating
                  ? buttonSmall
                  : { ...buttonSmall, ...buttonDisabled }}>
                {generating ? '⏳ Working…' : '↻ Re-assemble from client copy'}
              </button>
              <span style={{ color: INK.secondary, fontSize: '12px' }}>
                Assembled VERBATIM from the client's copy, questions and opt-in/privacy
                language — no AI. Re-assemble after editing any of them in Setup.
              </span>
            </>
          ) : copyFromAssets ? (
            // Landing page build has no strategy: the pages go out with the
            // client's copy from Client assets (staging pass, 2026-10-23).
            <span style={{ color: INK.secondary, fontSize: '12px' }}>
              Each page goes to the build team with the client&rsquo;s own copy from Client assets,
              word for word. To review it here as page copy first, use Assemble the pages on the Content plan.
            </span>
          ) : (
            <>
              <button onClick={generate} disabled={generating || !approved || planPages.length === 0}
                style={approved && planPages.length > 0 && !generating
                  ? (pages.length ? buttonSmall : buttonPrimarySmall(HUES.lp.main))
                  : { ...(pages.length ? buttonSmall : buttonPrimarySmall()), ...buttonDisabled }}>
                {generating ? '⏳ Working…' : (pages.length ? '↻ Recreate page copy' : 'Create page copy')}
              </button>
              <span style={{ color: INK.secondary, fontSize: '12px' }}>
                Taken VERBATIM from the strategy document's copy and questions — one
                hand-off per planned page ({planPages.length || 'no'} page{planPages.length === 1 ? '' : 's'});
                recreate it after editing the strategy.
              </span>
            </>
          )}
        </div>
        {generating && <GenerationProgress messages={LP_COPY_PROGRESS_MSGS} />}
        {msg && <div style={{ color: STATE.danger.text, fontSize: '12px', marginBottom: '6px' }}>{msg}</div>}

        {pages.length === 0 && !generating ? (
          <div style={{ color: INK.secondary, fontSize: '13px' }}>
            {copyFromAssets
              ? 'Not assembled yet — nothing is missing: the client\u2019s copy on each asset is what the build team receives.'
              : embedded
              ? 'No page copy yet — it is created from the strategy once the strategy is approved (by the client, or accepted internally), or assembled from the client\u2019s own copy on the Content plan.'
              : 'No landing-page copy yet — a syndication project assembles it from the Content plan; a strategy project extracts it here once the strategy stands.'}
          </div>
        ) : (
          pages.map((p, i) => (
            <div key={i} style={{ border: `1px solid ${NEUTRAL.border}`, borderRadius: '6px', padding: '12px 16px', margin: '8px 0', maxWidth: '820px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '14px' }}>{p.page_name}</strong>
                {p.audience && <span style={{ color: INK.secondary, fontSize: '12px' }}>{p.audience}</span>}
                <button onClick={() => downloadFileBlob(
                  `${API}/contentiq/projects/${project.id}/outputs/lp-copy/${i}/docx`,
                  `Page copy - ${p.page_name}.docx`)}
                  style={{ ...buttonSmall, marginLeft: 'auto' }}>
                  ⬇ .docx
                </button>
              </div>
              {(p.stages || []).map((st, si) => {
                // A branching product's interactive page is one page for every
                // path; the copy repeats it per path, so later paths point back
                // at the first instead of printing it again (2026-10-22).
                const sharedWith = sharedEarlier(i, st);
                return (
                <div key={si} style={{ marginTop: '8px' }}>
                  {/* "Page N" for a web page, "Engagement Phase N" for an email
                      or call — the server labels them (2026-10-28: a pair's
                      follow-up email read "Page 2", where page 2 is the asset).
                      "Stage" stays reserved for program stages. */}
                  <div style={{ fontWeight: 600, fontSize: '12px', color: HUES.lp.text }}>
                    {st.label || `Page ${si + 1}`}: {st.page}
                  </div>
                  {sharedWith ? (
                    <div style={{ margin: '4px 0 4px 8px', fontSize: '12.5px', color: INK.secondary }}>
                      The same page as under &ldquo;{sharedWith}&rdquo; — one page for every path.
                    </div>
                  ) : (st.blocks || []).map((b, bi) => (
                    <div key={bi} style={{ margin: '4px 0 4px 8px', fontSize: '13px' }}>
                      <span style={{ fontWeight: 600 }}>{b.label}</span>
                      <div style={{ whiteSpace: 'pre-wrap', color: INK.body }}>{b.text}</div>
                    </div>
                  ))}
                </div>
                );
              })}
            </div>
          ))
        )}
        {lpCopy?.generated_at && pages.length > 0 && (
          <div style={{ color: INK.muted, fontSize: '11px', marginTop: '4px' }}>
            Generated {new Date(lpCopy.generated_at).toLocaleString()}
          </div>
        )}
      </div>

    </div>
  );
}

// The workspace, rebuilt as a plain project LIST (Edward, 2026-08-27:
// "it's ok to show a list, but when going into a project, we should always
// use the rail interface"). The stage-card/panel workspace this replaced
// lives in git history; the panels above stay — the rail screen hosts them.
// Filter chips over the one projects list (Edward, 2026-10-22: a build
// package is just a project's later phase — one interface, not a separate
// board). A project with no package yet is still in content.
const PROJECT_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'content', label: 'In content' },
  { key: 'yours', label: 'Your move', tone: STATE.gate },
  { key: 'builder', label: 'With the build team', tone: HUES.strategy },
  { key: 'client', label: 'With the client', tone: HUES.scripts },
  { key: 'launched', label: 'Launched', tone: STATE.done },
];

export default function ContentIQ() {
  // The all-projects list, on the shared DataTable like Goals (Edward,
  // 2026-09-23: "shouldn't we use a shared component here, like goals?") —
  // sortable columns + a delete, replacing the hand-rolled row list. Since
  // 2026-10-22 it also carries each project's build package: who has the
  // ball and for how long, ETA, pages — the PM's single view.
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const [projects, setProjects] = useState([]);
  const [packages, setPackages] = useState({});   // project_id -> board row
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    axios.get(`${API}/contentiq/projects/`).then(r => setProjects(r.data))
      .catch(() => setLoadErr('Couldn\u2019t load your projects — refresh the page to try again.'));
    axios.get(`${API}/contentiq/packages`)
      .then(r => setPackages(Object.fromEntries((r.data.packages || []).map(p => [p.project_id, p]))))
      .catch(() => setLoadErr(e => e || 'Couldn\u2019t load the build packages — the package columns may be blank. Refresh to try again.'));
  }, []);
  const bucketOf = (pj) => (packages[pj.id] ? pmBucket(packages[pj.id]) : 'content');

  const att0 = (pj) => (pj.attachments || [])[0] || {};
  // The 7-step ladder (design settled 2026-10-08) when the backend sends it;
  // the old done-flag heuristic as fallback for anything without one.
  const statusOf = (pj) => {
    if (pj.stage?.label) return pj.stage.label;
    const s4 = (pj.stages || []).find(st => st.stage_number === 4);
    return s4?.done ? 'complete' : 'in progress';
  };

  const removeProject = async (pj) => {
    if (!window.suppressConfirm &&
        !window.confirm(`Delete “${pj.name}”? It disappears from ContentIQ; uploaded files are kept and an admin can restore it by hand.`)) return;
    try {
      await axios.delete(`${API}/contentiq/projects/${pj.id}`);
      setProjects(list => list.filter(x => x.id !== pj.id));
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Could not delete the project.');
    }
  };

  const needle = q.trim().toLowerCase();
  const shown = projects
    .filter(pj => filter === 'all' || bucketOf(pj) === filter)
    .filter(pj => {
      if (!needle) return true;
      const att = att0(pj);
      return [pj.name, att.client_name, att.program_name, pj.product_format_name]
        .some(v => (v || '').toLowerCase().includes(needle));
    });
  const counts = projects.reduce((c, pj) => ({ ...c, [bucketOf(pj)]: (c[bucketOf(pj)] || 0) + 1 }),
    { all: projects.length });
  const ballAge = (pj) => ballAgeDays(packages[pj.id] || {});

  // Most recently edited first by default (Edward, 2026-10-15: "have date
  // last edited so we can see how recent things are (and sort)") — matches
  // the order the backend already returns. Any header click re-sorts.
  const { sorted, toggle, field, dir } = useSort(shown, 'edited', 'desc', { key: {
    client: pj => att0(pj).client_name || '',
    program: pj => att0(pj).program_name || '',
    format: pj => pj.product_format_name || '',
    // Sort the Stage column by ladder position, not label text.
    status: pj => pj.stage?.number ?? 0,
    edited: pj => pj.updated_at || '',
    ball: pj => ballAge(pj),
  } });

  // Every column carries a real header and an explicit width — the two
  // trailing header-less columns made resizing confusing and let the Stage
  // chip overlap them on first open (Edward, 2026-10-15). The widths fit the
  // page without sideways scrolling, and the whole row opens the project
  // (usability pass, 2026-10-22: "Open" sat off-screen in the 9th column).
  const openProject = (pj) => navigate(`/contentiq/project/${pj.id}`);
  const columns = [
    { key: 'name', header: 'Project', sortable: true, width: 190, render: pj => (
        <button onClick={e => { e.stopPropagation(); openProject(pj); }}
          style={{ ...buttonLink, fontSize: '13px', fontWeight: 700 }}>{pj.name}</button>) },
    { key: 'client', header: 'Client', sortable: true, width: 100, render: pj => att0(pj).client_name || '—' },
    { key: 'program', header: 'Program', sortable: true, width: 140, render: pj => att0(pj).program_name || '—' },
    { key: 'format', header: 'Product', sortable: true, width: 110, render: pj => pj.product_format_name || '—' },
    { key: 'status', header: 'Stage', sortable: true, width: 150, render: pj => {
        if (pj.stage) return <CiqStageChip stage={pj.stage} />;
        const s = statusOf(pj);
        return (
          <span style={{ fontSize: '11px', fontWeight: 600, borderRadius: '10px', padding: '2px 10px',
                         background: s === 'complete' ? STATE.done.tint : NEUTRAL.chipBg,
                         color: s === 'complete' ? STATE.done.text : INK.secondary }}>
            {s}
          </span>
        );
      } },
    { key: 'ball', header: 'Who has the ball', sortable: true, width: 140, render: pj => {
        const p = packages[pj.id];
        if (!p) return <span style={{ fontSize: '12px', color: INK.muted }}>—</span>;
        return <CiqBallChips ball={p.ball} perspective="pm" names={ballNames(p, { currentUserId: currentUser?.id })} />;
      } },
    { key: 'package', header: 'Package', width: 160, render: pj => {
        const p = packages[pj.id];
        if (!p) return <span style={{ fontSize: '12px', color: INK.muted }}>not yet</span>;
        const pg = p.pages || {};
        return (
          <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '12px', color: INK.body }}>
            <span>{p.version ? `v${p.version} · ${p.status_label}` : 'Ready to send'}{p.include_edm ? ' · + email' : ''}</span>
            {pg.total > 0 && <span style={{ color: INK.secondary }}>{pg.accepted || 0} of {pg.total} accepted</span>}
            {p.eta && (
              <span style={{ color: p.eta_passed ? STATE.danger.text : INK.secondary }}>
                ETA {fmtDate(p.eta)}{p.eta_passed ? ' — passed' : ''}
              </span>
            )}
          </span>
        );
      } },
    { key: 'edited', header: 'Last edited', sortable: true, width: 140, nowrap: true,
      render: pj => <span style={{ fontSize: '12px', color: INK.secondary }}>{fmtDate(pj.updated_at)}</span> },
    { key: 'actions', header: 'Actions', width: 70, render: pj => (
        <button onClick={e => { e.stopPropagation(); removeProject(pj); }} title="Delete this project"
          aria-label={`Delete ${pj.name}`}
          style={{ border: 'none', background: 'none', color: INK.muted, cursor: 'pointer', padding: 0 }}>✕</button>) },
  ];

  return (
    <div style={{ ...pageShell, padding: '30px 48px' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', fontFamily: FONTS.body }}>
        <button onClick={() => navigate('/contentiq')} style={{ ...buttonLink, fontSize: '13px', marginBottom: '8px' }}>
          ← ContentIQ Home
        </button>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', marginBottom: '14px' }}>
          <div style={{ ...displayFont('24px', 700) }}>All projects</div>
          <span style={{ flexGrow: 1 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by name, client, program…"
            style={{ padding: '7px 10px', fontSize: '13px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.control}`, borderRadius: RADII.small, width: '240px' }} />
          <button onClick={() => navigate('/contentiq/new')} style={buttonPrimarySmall()}>+ New project</button>
        </div>
        {loadErr && <div role="alert" style={{ fontSize: '12.5px', color: STATE.danger.text, marginBottom: '10px' }}>{loadErr}</div>}
        <div role="group" aria-label="Filter projects" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
          {PROJECT_FILTERS.map(f => {
            const active = filter === f.key;
            const tone = f.tone || { deep: INK.body, tint: NEUTRAL.band, border: NEUTRAL.control, main: INK.body };
            return (
              <button key={f.key} onClick={() => setFilter(f.key)} aria-pressed={active}
                style={{ fontFamily: FONTS.body, fontSize: '12.5px', fontWeight: active ? 700 : 600,
                  padding: '6px 12px', borderRadius: RADII.control, cursor: 'pointer',
                  border: `${active ? 2 : 1}px solid ${active ? (tone.main || tone.deep) : tone.border}`,
                  background: active ? tone.tint : NEUTRAL.pageBg, color: tone.deep || INK.body }}>
                {f.label} · {counts[f.key] || 0}
              </button>
            );
          })}
        </div>
        <DataTable
          rows={sorted}
          rowKey={pj => pj.id}
          columns={columns}
          sort={{ field, dir, onSort: toggle }}
          onRowClick={openProject}
          emptyText={loadErr ? 'Couldn\u2019t load projects.'
            : projects.length === 0 ? 'No projects yet — click + New project.' : 'Nothing in this group.'}
        />
      </div>
    </div>
  );
}
